(function () {
  'use strict';

  /* ============================================
     LOCALE-AWARE PATHS
     Shopify serves secondary languages under a prefix: Shopify.routes.root is
     "/" in English but "/ar/" in Arabic. Liquid links get this for free via
     routes.*; paths built here in JS do NOT — a bare '/pages/lesson' silently
     drops an Arabic visitor back onto the English site (proven on the /ar
     preview: the Liquid nav rendered /ar/collections/courses while our JS
     rendered /collections/courses on the same page).
     Cart AJAX (/cart/*.js) and the app proxy (/apps/saif/*) are deliberately
     NOT routed through this — they are session/proxy endpoints, not navigation.
     ============================================ */
  function saifPath(p) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/+$/, '') + p;
  }

  /* ============================================
     PROGRESS TRACKER (localStorage)
     ============================================ */
  var SaifProgress = {
    _key: function (customerId, courseHandle) {
      return 'saif_progress_' + customerId + '_' + courseHandle;
    },

    get: function (customerId, courseHandle) {
      try {
        var data = localStorage.getItem(this._key(customerId, courseHandle));
        return data ? JSON.parse(data) : { completedLessons: [], lastLesson: null };
      } catch (e) {
        return { completedLessons: [], lastLesson: null };
      }
    },

    save: function (customerId, courseHandle, data) {
      try {
        localStorage.setItem(this._key(customerId, courseHandle), JSON.stringify(data));
      } catch (e) { /* storage full or unavailable */ }
    },

    markComplete: function (customerId, courseHandle, lessonId) {
      var data = this.get(customerId, courseHandle);
      var alreadyDone = data.completedLessons.indexOf(lessonId) !== -1;
      if (!alreadyDone) {
        data.completedLessons.push(lessonId);
      }
      data.lastLesson = lessonId;
      this.save(customerId, courseHandle, data);

      // Mirror to backend so streak ticks and progress syncs across devices
      if (window.SaifSync && window.SaifSync.ready) {
        window.SaifSync.progress.markComplete(courseHandle + '::' + lessonId);
      }
      // Track only the first completion of this lesson — re-completes are
      // common (re-watching) and would distort funnel metrics.
      if (!alreadyDone && window.SaifTrack) {
        window.SaifTrack('lesson_completed', {
          course_handle: courseHandle,
          lesson_id: lessonId,
          lesson_index: lessonIndexFor(courseHandle, lessonId),
          total_completed: data.completedLessons.length
        });
      }
    },

    toggleComplete: function (customerId, courseHandle, lessonId) {
      var data = this.get(customerId, courseHandle);
      var idx = data.completedLessons.indexOf(lessonId);
      if (idx === -1) {
        data.completedLessons.push(lessonId);
      } else {
        data.completedLessons.splice(idx, 1);
      }
      this.save(customerId, courseHandle, data);

      // Only mirror "completed" events to backend (un-complete is local-only)
      if (idx === -1 && window.SaifSync && window.SaifSync.ready) {
        window.SaifSync.progress.markComplete(courseHandle + '::' + lessonId);
      }
      // Track completion (same payload as markComplete — the worker's
      // activation gate reads lesson_index off this event).
      if (idx === -1 && window.SaifTrack) {
        window.SaifTrack('lesson_completed', {
          course_handle: courseHandle,
          lesson_id: lessonId,
          lesson_index: lessonIndexFor(courseHandle, lessonId),
          total_completed: data.completedLessons.length
        });
      }

      return idx === -1;
    },

    /**
     * Pull the global completed list from SaifSync and seed local storage so
     * progress shows correctly on a new device. Idempotent — safe to call any time.
     */
    hydrateFromBackend: function (customerId, courseHandle) {
      if (!window.SaifSync || !window.SaifSync.ready) return Promise.resolve();
      var self = this;
      return window.SaifSync.progress.get().then(function (r) {
        if (!r || !r.ok || !r.progress) return;
        var prefix = courseHandle + '::';
        var serverCompleted = (r.progress.completed || [])
          .filter(function (k) { return k.indexOf(prefix) === 0; })
          .map(function (k) { return k.slice(prefix.length); });
        if (!serverCompleted.length) return;
        var local = self.get(customerId, courseHandle);
        var merged = local.completedLessons.slice();
        serverCompleted.forEach(function (id) {
          if (merged.indexOf(id) === -1) merged.push(id);
        });
        local.completedLessons = merged;
        self.save(customerId, courseHandle, local);
      });
    },

    setLastLesson: function (customerId, courseHandle, lessonId) {
      var data = this.get(customerId, courseHandle);
      data.lastLesson = lessonId;
      this.save(customerId, courseHandle, data);
    },

    getPercentage: function (customerId, courseHandle, totalLessons) {
      var data = this.get(customerId, courseHandle);
      if (totalLessons <= 0) return 0;
      return Math.round((data.completedLessons.length / totalLessons) * 100);
    },

    isCompleted: function (customerId, courseHandle, lessonId) {
      var data = this.get(customerId, courseHandle);
      return data.completedLessons.indexOf(lessonId) !== -1;
    }
  };

  /* ============================================
     HELPERS
     ============================================ */
  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Look up a JSON-payload <script> element by id-prefix. Sections that may
  // be rendered multiple times suffix their ids with `-{{ section.id }}`,
  // so the strict getElementById form misses them.
  function readJSONByPrefix(idPrefix) {
    var el = document.querySelector('[id^="' + idPrefix + '"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function flattenLessons(curriculum) {
    var lessons = [];
    if (!curriculum || !curriculum.modules) return lessons;
    curriculum.modules.forEach(function (mod) {
      if (!mod.lessons) return;
      mod.lessons.forEach(function (lesson) {
        lessons.push({
          id: lesson.id,
          title: lesson.title,
          duration: lesson.duration,
          video_url: lesson.video_url,
          description: lesson.description,
          content: lesson.content,
          resources: lesson.resources,
          free_preview: !!lesson.free_preview,
          // `accessible` is computed in Liquid (purchased OR free_preview)
          // and gates whether the iframe renders. Default true if absent
          // so older payloads don't all lock — server is authoritative.
          accessible: lesson.accessible !== false,
          moduleTitle: mod.title,
          moduleId: mod.id
        });
      });
    });
    return lessons;
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // 1-based position of a lesson in the flattened curriculum (0 = unknown).
  // The worker's lesson_completed activation gate expects a numeric
  // `lesson_index` where 1 means the first lesson of the course.
  function lessonIndexFor(courseHandle, lessonId) {
    var courseData = readJSON('SaifCourseData');
    var course = courseData && courseData[courseHandle];
    if (!course) return 0;
    var curriculum = parseCurriculum(course.curriculum);
    if (!curriculum) return 0;
    var all = flattenLessons(curriculum);
    for (var i = 0; i < all.length; i++) {
      if (all[i].id === lessonId) return i + 1;
    }
    return 0;
  }

  // Parse a human duration string ("3 min", "12:34", "1h 5min") into
  // seconds. 0 when unparseable. A bare number is treated as minutes.
  function parseDurationSeconds(str) {
    if (!str) return 0;
    str = String(str).trim();
    var parts = str.split(':');
    if (parts.length > 1 && parts.every(function (p) { return /^\d+$/.test(p.trim()); })) {
      var secs = 0;
      parts.forEach(function (p) { secs = secs * 60 + parseInt(p, 10); });
      return secs;
    }
    var h = str.match(/(\d+)\s*h/i);
    var m = str.match(/(\d+)\s*m/i);
    var s = str.match(/(\d+)\s*s/i);
    var total = 0;
    if (h) total += parseInt(h[1], 10) * 3600;
    if (m) total += parseInt(m[1], 10) * 60;
    if (s) total += parseInt(s[1], 10);
    if (!total && /^\d+$/.test(str)) total = parseInt(str, 10) * 60;
    return total;
  }

  // Friendly error state for a bad/unknown lesson — replaces the infinite
  // "Loading lesson..." spinner with a message + a way back, and disables
  // the controls that have nothing to act on.
  function renderLessonNotFound(courseHandle) {
    var video = document.getElementById('SaifLessonVideo');
    if (video) {
      var backUrl = courseHandle ? saifPath('/products/' + encodeURIComponent(courseHandle)) : saifPath('/collections/all');
      var i18n = window.SaifI18n || {};
      var backLabel = courseHandle
        ? (i18n.back_to_course || 'Back to the course')
        : (i18n.browse_courses || 'Browse courses');
      video.classList.add('saif-lesson__video--empty');
      video.innerHTML = '<div class="saif-lesson__video-placeholder"><p style="color:rgba(255,255,255,0.6)">' + (i18n.lesson_not_found || 'Lesson not found.') + ' <a href="' + backUrl + '" style="color:inherit;text-decoration:underline">' + backLabel + '</a>.</p></div>';
    }
    ['SaifLessonControls', 'SaifLessonNotepad'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) { el.hidden = true; el.style.display = 'none'; }
    });
    // Stop the sidebar spinner too — an empty list reads better than an
    // infinite loader when there's nothing to load.
    var sidebar = document.getElementById('SaifLessonSidebarContent');
    if (sidebar) sidebar.innerHTML = '';
    var backLink = document.getElementById('SaifLessonBackLink');
    if (backLink && courseHandle) backLink.href = saifPath('/products/' + encodeURIComponent(courseHandle));
  }

  // Locked lessons show only the paywall card — hide the below-the-fold
  // toolkit (resource tabs, practice studio, audio compare, complete
  // button) so the page focuses on the enroll CTA. Inline display:none
  // in addition to [hidden] because some of these containers carry
  // stylesheet display rules that beat the hidden attribute.
  function hideLessonToolkit() {
    ['SaifLessonResourceTabs', 'SaifPracticeStudio', 'SaifLessonCompare', 'SaifLessonControls'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) { el.hidden = true; el.style.display = 'none'; }
    });
  }

  /* ============================================
     LESSON PAGE
     ============================================ */
  function initLessonPage() {
    var courseData = readJSON('SaifCourseData');
    // Lesson section renders the customer JSON with a section.id suffix
    // (id="SaifCourseCustomer-XXX") so a strict getElementById('SaifCourseCustomer')
    // returns null. Prefix-match lookup matches both suffixed and bare ids.
    var customer = readJSONByPrefix('SaifCourseCustomer');
    var config = readJSON('SaifLessonConfig');

    if (!customer || !config) return;

    // Read URL params
    var params = new URLSearchParams(window.location.search);
    var courseHandle = params.get('course');
    var lessonId = params.get('lesson');

    if (!courseHandle || !lessonId) {
      renderLessonNotFound(courseHandle);
      return;
    }

    // Hydrate cross-device progress before we render the curriculum sidebar
    var hydration = customer.loggedIn
      ? SaifProgress.hydrateFromBackend(customer.customerId, courseHandle)
      : null;

    var course = courseData ? courseData[courseHandle] : null;
    if (!course) {
      // Course not embedded — either the handle is wrong, or it isn't
      // listed in the section's `preview_course_handles` setting and the
      // visitor isn't logged in / hasn't purchased it.
      var video = document.getElementById('SaifLessonVideo');
      if (video) video.innerHTML = '<div class="saif-lesson__video-placeholder"><p style="color:rgba(255,255,255,0.6)">Course not found. <a href="' + saifPath('/collections/all') + '" style="color:inherit;text-decoration:underline">Browse courses</a>.</p></div>';
      return;
    }

    var curriculum = parseCurriculum(course.curriculum);
    if (!curriculum) {
      var v2 = document.getElementById('SaifLessonVideo');
      if (v2) v2.innerHTML = '<div class="saif-lesson__video-placeholder"><p style="color:rgba(255,255,255,0.6)">Could not load lesson data. Please try refreshing.</p></div>';
      return;
    }

    loadLesson(curriculum, course, courseHandle, lessonId, customer, config);

    // On a fresh device localStorage is empty but the backend holds the real
    // progress; hydrateFromBackend() merges it in asynchronously. Once it
    // resolves, re-render the sidebar and refresh the complete-button state so
    // completed lessons aren't left showing as incomplete. Mirrors initDashboard.
    if (hydration && hydration.then) {
      hydration.then(function () {
        renderSidebar(curriculum, lessonId, courseHandle, customer.customerId || 'preview');
        if (customer.loggedIn) {
          var completeBtn = document.getElementById('SaifLessonCompleteBtn');
          if (completeBtn) {
            updateCompleteButton(completeBtn, SaifProgress.isCompleted(customer.customerId, courseHandle, lessonId), config);
          }
        }
      }).catch(function () { /* keep the localStorage pass on failure */ });
    }
  }

  function parseCurriculum(data) {
    if (!data) return null;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch (e) { return null; }
    }
    if (typeof data === 'object' && data.modules) return data;
    return null;
  }

  /**
   * Per-module practice reference clips, served from the saif-maqam-clips
   * R2 bucket through the Worker (immutable edge cache, $0 egress, Range
   * support) — the same pipeline as the Maqam-478 library. The map is
   * static in this file rather than in metafields: which modules have
   * drill clips is fixed course content, so bundling it avoids admin-API
   * round-trips and keeps the lesson page fast.
   */
  var DRILL_REF_BASE = 'https://api.saifvoice.net/maqam-clips/';
  var DRILL_REFS = (function () {
    function clip(short, mod, drill) {
      return {
        label: drill ? 'Drill ' + drill : 'Module ' + mod + ' reference',
        url: DRILL_REF_BASE + 'drill-' + short + '-mod' + mod + (drill ? '-' + drill : '') + '.mp3?v=2' // v=2: July 2026 re-recorded set replaced same-named files; busts browser caches
      };
    }
    function mods(short, list) {
      var out = {};
      list.forEach(function (m) { out['lesson-' + m] = [clip(short, m)]; });
      return out;
    }
    var map = {
      // Full per-module coverage (owner's July 2026 re-recorded set):
      // every module ships one reference clip, incl. the new Module 1s.
      'foundation-of-recitation': mods('foundation', [1, 2, 3, 4, 5, 6, 7, 8]),
      'precision-of-recitation': mods('precision', [1, 2, 3, 4, 5, 6, 7, 8]),
      'mastery-of-recitation': mods('mastery', [1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
    };
    return map;
  })();

  /**
   * Point the embedded Practice Studio at this lesson's reference clip(s).
   * One clip loads silently; multiple clips also render a chip picker
   * above the studio. A reference_audio array on the lesson (from the
   * curriculum metafield) overrides the static map if ever present.
   * The studio constructs in its own DOMContentLoaded handler, so retry
   * briefly if it hasn't initialized yet.
   */
  function applyLessonReference(lesson, courseHandle) {
    var refs = (lesson && lesson.reference_audio && lesson.reference_audio.length)
      ? lesson.reference_audio
      : ((DRILL_REFS[courseHandle] || {})[lesson && lesson.id] || []);
    if (lesson && lesson.accessible === false) refs = [];
    var attempts = 0;
    (function apply() {
      var studio = window.SaifPracticeStudio;
      var picker = document.getElementById('SaifLessonRefPicker');
      if (!studio) {
        if (attempts++ < 40) { setTimeout(apply, 250); }
        return;
      }
      /* Liquid cannot read the lesson query parameter, so hand the resolved
         curriculum lesson to the shared Studio here. Saved takes and Personal
         Review submissions then retain the lesson they came from, just like
         recordings created by a lesson-specific Studio would. Do this before
         the no-reference return: the free intro lesson can have no drill clip
         and still needs correct recording metadata. */
      var mount = studio.container;
      if (!mount) return;
      mount.setAttribute('data-lesson-id', (lesson && lesson.id) || '');
      if (!refs.length || !refs[0] || !refs[0].url) {
        if (picker) picker.remove();
        return;
      }
      if (!mount || !mount.parentNode) return;
      if (!picker) {
        picker = document.createElement('div');
        picker.id = 'SaifLessonRefPicker';
        picker.className = 'saif-lesson__ref-picker';
        mount.parentNode.insertBefore(picker, mount);
      }
      function select(i) {
        studio.loadReference(refs[i].url, {
          clipTitle: (lesson.title || 'Lesson') + ' — ' + (refs[i].label || 'reference')
        });
        Array.prototype.forEach.call(picker.querySelectorAll('button'), function (b, j) {
          b.setAttribute('aria-pressed', j === i ? 'true' : 'false');
        });
      }
      if (refs.length > 1) {
        picker.hidden = false;
        picker.innerHTML = '<span class="saif-lesson__ref-picker-label">Practice drills for this module:</span>';
        refs.forEach(function (r, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'saif-lesson__ref-picker-chip';
          b.textContent = r.label || ('Drill ' + (i + 1));
          b.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
          b.addEventListener('click', function () { select(i); });
          picker.appendChild(b);
        });
      } else {
        picker.hidden = true;
        picker.innerHTML = '';
      }
      select(0);
    })();
  }

  function loadLesson(curriculum, course, courseHandle, lessonId, customer, config) {

    var allLessons = flattenLessons(curriculum);
    var currentIndex = -1;
    for (var i = 0; i < allLessons.length; i++) {
      if (allLessons[i].id === lessonId) { currentIndex = i; break; }
    }

    if (currentIndex === -1) {
      // Unknown lesson id — friendly error instead of an infinite spinner.
      // The curriculum itself is valid, so still render the sidebar to
      // give the visitor a way into a real lesson.
      renderLessonNotFound(courseHandle);
      renderSidebar(curriculum, lessonId, courseHandle, customer.customerId || 'preview');
      var notFoundTitle = document.getElementById('SaifLessonCourseTitle');
      if (notFoundTitle) notFoundTitle.textContent = course.title || courseHandle;
      return;
    }

    var lesson = allLessons[currentIndex];

    // Track last-viewed lesson regardless of login state. For non-logged-in
    // visitors we use a "guest" id so the resume banner on the homepage /
    // course-detail page can pick this up later. Server-side hydration only
    // happens for logged-in users (handled above).
    var progressId = customer.loggedIn ? customer.customerId : 'guest';
    SaifProgress.setLastLesson(progressId, courseHandle, lessonId);

    // Update back link and course title
    var backLink = document.getElementById('SaifLessonBackLink');
    if (backLink) backLink.href = saifPath('/products/' + courseHandle);

    var courseTitleEl = document.getElementById('SaifLessonCourseTitle');
    if (courseTitleEl) courseTitleEl.textContent = course.title || courseHandle;

    // Gate on `accessible` — non-accessible lessons get an upsell card,
    // not the video iframe. Liquid already strips video_url for these,
    // so even a buggy frontend can't reveal paywalled content.
    if (lesson.accessible === false) {
      renderLockedLesson(lesson, course, courseHandle, document.getElementById('SaifLessonVideo'), allLessons);
      hideLessonToolkit();
      if (window.SaifTrack) window.SaifTrack('lesson_locked_view', {
        course_handle: courseHandle,
        lesson_id: lesson.id,
        lesson_title: lesson.title
      });
    } else {
      var lessonContext = {
        course_handle: courseHandle,
        lesson_id: lesson.id,
        lesson_title: lesson.title,
        purchased: !!(course && course.purchased)
      };
      if (window.SaifTrack) window.SaifTrack('lesson_viewed', lessonContext);
      renderVideo(lesson.video_url, document.getElementById('SaifLessonVideo'), {
        /* SAIF_END_MODAL_V1 — purchasers get null, exactly as before: nothing to sell them. */
        onEnded: (course && course.purchased) ? null : function () {
          try { document.dispatchEvent(new CustomEvent('saif:lesson-ended')); } catch (e) {}
          showEndModal(course, courseHandle, lesson.id, lessonContext);
        },
        context: lessonContext,
        durationSeconds: parseDurationSeconds(lesson.duration),
        /* SAIF_AUTOPLAY_CALLSITE — free lesson only.
           A paying student stepping through lessons does not want video starting under
           them. A first-time visitor routed here from the quiz does, and they are the
           population the quiz_destination A/B test is measuring. */
        autoplay: !(course && course.purchased) && lesson.id === 'lesson-0',
        /* SAIF_LESSON_SOUND_V2 (owner, 2026-08-04) — no "Tap for sound" button. Sound should
           just arrive.

           ⚠️ What cannot change: every browser blocks UNMUTED autoplay without a recent user
           activation on this origin, and activation does not survive a navigation. The visitor
           reached this page by clicking on the previous one, which does not count. So the
           unmuted attempt in renderVideo() still fails for most arrivals and playback still
           falls back to muted. No site setting overrides that.

           What DOES change: instead of asking them to aim at a button, the very first real
           interaction anywhere on the page turns the sound on — a click, a tap, a key. That is
           the same gesture the old button needed, minus the aiming, so in practice sound comes
           on within a second or two for anyone who touches the page at all.

           The listeners are one-shot and passive, and only gestures that browsers count as
           activation are used: scroll deliberately is NOT one of them, so it is not listened
           for. If the visitor never interacts, the lesson plays muted — which is the trade the
           owner chose over showing a prompt. */
        onMutedAutoplay: function (player) {
          if (window.__saifUnmuteArmed) return;
          window.__saifUnmuteArmed = true;
          var EVENTS = ['pointerdown', 'touchend', 'keydown', 'click'];
          var done = false;
          function unmute() {
            if (done) return;
            done = true;
            EVENTS.forEach(function (ev) { document.removeEventListener(ev, unmute, true); });
            try { player.unMute(); player.playVideo(); } catch (e) {}
            if (window.SaifTrack) window.SaifTrack('lesson_video_unmuted', lessonContext);
          }
          /* Capture phase: a click that lands on a control which stops propagation still counts. */
          EVENTS.forEach(function (ev) { document.addEventListener(ev, unmute, true); });
        }
      });
      // MUST come after renderVideo(): it hides the post-lesson block on entry to reset
      // state for lesson navigation, so showing it first would be undone immediately.
      // The block is a permanent part of the page for anyone who hasn't bought the
      // course, and stays hidden entirely from purchasers (nothing to sell them).
      if (!(course && course.purchased)) showEndCta(course, courseHandle, lesson.id);
    }

    // Render info
    var titleEl = document.getElementById('SaifLessonTitle');
    var descEl = document.getElementById('SaifLessonDescription');
    var contentEl = document.getElementById('SaifLessonContent');

    if (titleEl) titleEl.textContent = lesson.title;
    if (descEl) descEl.textContent = lesson.description || '';
    if (contentEl) contentEl.innerHTML = lesson.content || '';

    // Render resources
    renderResources(lesson.resources);

    // Point the embedded Practice Studio at this module's reference clip(s)
    applyLessonReference(lesson, courseHandle);

    // Load student notes (only for accessible lessons + logged-in customers)
    if (customer.loggedIn && lesson.accessible !== false) {
      initNotes(customer.customerId, courseHandle, lessonId);
    } else {
      var notepad = document.getElementById('SaifLessonNotepad');
      if (notepad) notepad.hidden = true;
    }

    // Render sidebar
    renderSidebar(curriculum, lessonId, courseHandle, customer.customerId || 'preview');

    // Render navigation
    renderLessonNav(allLessons, currentIndex, courseHandle);

    // Bind complete button
    if (customer.loggedIn) {
      bindCompleteButton(customer.customerId, courseHandle, lessonId, config);
    } else {
      var controls = document.getElementById('SaifLessonControls');
      if (controls) controls.hidden = true;
    }

    // Bind mobile sidebar
    bindMobileSidebar();

    // Update page title
    document.title = lesson.title + ' — ' + (course.title || '');
  }

  function initNotes(customerId, courseHandle, lessonId) {
    var input = document.getElementById('SaifLessonNotesInput');
    var saved = document.getElementById('SaifLessonNotesSaved');
    var status = document.getElementById('SaifLessonNotesStatus');
    var saveBtn = document.getElementById('SaifLessonNotesSaveBtn');
    var countEl = document.getElementById('SaifLessonNotesCount');
    if (!input) return;

    var sync = window.SaifSync;
    // Composite lesson key (course + lesson) so the same lesson ID under
    // different courses doesn't clobber.
    var noteKey = courseHandle + '::' + lessonId;
    // Local fallback key (matches the old format so no data is lost on migration)
    var lsKey = 'saif_notes_' + customerId + '_' + courseHandle + '_' + lessonId;

    function setState(state) {
      if (!status) return;
      status.dataset.state = state;
      // Concise labels — the notepad lives in the slim right rail of the
      // editorial lesson layout, where long phrases get truncated.
      if (state === 'idle')         status.textContent = (window.SaifI18n && window.SaifI18n.notes_status_saved) || 'Saved';
      else if (state === 'unsaved') status.textContent = (window.SaifI18n && window.SaifI18n.notes_status_unsaved) || 'Unsaved';
      else if (state === 'saving')  status.textContent = 'Saving\u2026';
      else if (state === 'saved')   status.textContent = 'Saved \u2713';
      else if (state === 'error')   status.textContent = 'Retry\u2026';
    }

    function updateCount() {
      // Just the count — small caps 'CHARS' suffix is added via CSS so the rail label stays tight.
      if (countEl) countEl.textContent = String(input.value.length || 0);
    }

    // ---- Initial load: prefer remote, fall back to local ----
    function loadInitial() {
      if (sync && sync.ready) {
        sync.notes.getAll().then(function (r) {
          if (r && r.ok && r.notes) {
            // Either the composite key OR the bare lesson ID for older notes
            var v = r.notes[noteKey] || r.notes[lessonId];
            if (v != null) input.value = v;
          } else {
            var localOnly = localStorage.getItem(lsKey);
            if (localOnly) input.value = localOnly;
          }
          updateCount();
          setState('idle');
        });
      } else {
        var local = localStorage.getItem(lsKey);
        if (local) input.value = local;
        updateCount();
        setState('idle');
      }
    }

    // ---- Save (remote w/ local mirror, or local-only) ----
    function commitSave() {
      setState('saving');
      var text = input.value;

      // Always mirror to local storage so a 0-network state doesn't lose data
      try { localStorage.setItem(lsKey, text); } catch (e) {}

      var syncRequest;
      if (sync && sync.ready) {
        syncRequest = sync.notes.save(noteKey, text);
      } else {
        syncRequest = Promise.resolve({ ok: true, source: 'local' });
      }

      syncRequest.then(function (r) {
        if (r && r.ok) {
          setState('saved');
          if (saved) {
            saved.hidden = false;
            setTimeout(function () { saved.hidden = true; }, 1500);
          }
          setTimeout(function () {
            if (status && status.dataset.state === 'saved') setState('idle');
          }, 2000);
        } else {
          setState('error');
          console.error('[saif-notes] save failed:', r && r.error);
        }
      });
    }

    var saveTimer;
    input.addEventListener('input', function () {
      updateCount();
      setState('unsaved');
      clearTimeout(saveTimer);
      saveTimer = setTimeout(commitSave, 1200);
    });

    if (saveBtn) {
      saveBtn.addEventListener('click', function () {
        clearTimeout(saveTimer);
        commitSave();
      });
    }

    // Synchronously mirror to localStorage on unload so nothing is lost mid-typing.
    // (Network sync after unload isn't reliable — sendBeacon would be needed.)
    window.addEventListener('beforeunload', function () {
      if (status && status.dataset.state === 'unsaved') {
        try { localStorage.setItem(lsKey, input.value); } catch (e) {}
      }
    });

    loadInitial();
  }

  function renderResources(resources) {
    var container = document.getElementById('SaifLessonResources');
    var list = document.getElementById('SaifLessonResourcesList');
    if (!container || !list) return;

    if (!resources || !resources.length) {
      container.hidden = true;
      return;
    }

    var html = '';
    resources.forEach(function (res) {
      /* The Settling Breath PDF on the CDN is a 676-byte tombstone that only says
         "the workbook has moved" — so the free lesson's one download handed the
         reader a redirect note labelled "one-page guide · ~40 KB". Until the
         curriculum metafield is corrected in Shopify admin, send it to the real
         page and drop the false size (content audit, 2026-08). */
      if (typeof res.url === 'string' && /settling-breath\.pdf$/i.test(res.url)) {
        res = { title: res.title, url: '/pages/breath-pdf', type: 'link', size: '' };
      }
      var icon = res.type === 'download' ? '↓' : res.type === 'link' ? '→' : '•';
      var target = res.type === 'link' ? ' target="_blank" rel="noopener"' : '';
      var download = res.type === 'download' ? ' download' : '';
      html += '<li class="saif-lesson__resource-item">' +
        '<a href="' + escapeHtml(res.url) + '" class="saif-lesson__resource-link"' + target + download + '>' +
          '<span class="saif-lesson__resource-icon">' +
            (res.type === 'download'
              ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'
              : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>') +
          '</span>' +
          '<span class="saif-lesson__resource-name">' + escapeHtml(res.title) + '</span>' +
          (res.size ? '<span class="saif-lesson__resource-size">' + escapeHtml(res.size) + '</span>' : '') +
        '</a>' +
      '</li>';
    });

    list.innerHTML = html;
    container.hidden = false;
  }

  function renderVideo(videoUrl, container, opts) {
    if (!container) return;

    // Hide any prior post-lesson CTA + clear lingering Stream listeners
    // before we re-render — guards against double-binding on lesson nav.
    var endCta = document.getElementById('SaifLessonEndCta');
    if (endCta) endCta.hidden = true;

    if (!videoUrl) {
      // No video URL — drop the 16:9 black box and render a small inline
      // notice instead. The "--empty" modifier is what the CSS keys off
      // to remove the aspect ratio + dark background.
      container.classList.add('saif-lesson__video--empty');
      container.innerHTML =
        '<div class="saif-lesson__video-placeholder">' +
          '<div class="saif-lesson__video-placeholder-play">' +
            '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="8 5 20 12 8 19"/></svg>' +
          '</div>' +
          '<p>Video coming soon</p>' +
        '</div>';
      return;
    }

    container.classList.remove('saif-lesson__video--empty');

    // YouTube URLs (watch / share / shorts / embed) can't be iframed
    // verbatim — youtube.com/watch refuses framing. Normalize them to the
    // privacy-enhanced nocookie embed form before building the iframe.
    var ytMatch = videoUrl.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?[^#]*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    var ytId = null;
    if (ytMatch) {
      /* SAIF_END_MODAL_V1 — enablejsapi is the only way to hear "finished" from a
         cross-origin YouTube embed. Without it no completion event exists at all. */
      ytId = ytMatch[1];
      /* SAIF_AUTOPLAY_V2 — start MUTED from the URL, then ask for sound once it is playing.

         The old code did the opposite: it called unMute() + playVideo() from JS and gave the
         browser 700ms to reach state PLAYING. On a phone the player is still BUFFERING at
         700ms, so a perfectly healthy video was judged blocked — and the fallback then MUTED
         it. Measured over 150 real attempts: 68% "blocked" on Chrome/Android, where muted
         autoplay is always permitted; half of those videos started within 0.8s of the verdict;
         88% of the people it "failed" for watched anyway. The block was ours, and so was the
         silence.

         The fix: start playback explicitly from onReady, MUTED — the one form no browser
         refuses — and ask for sound afterwards, where failing costs nothing. */
      videoUrl = 'https://www.youtube-nocookie.com/embed/' + ytId +
        '?rel=0&modestbranding=1&playsinline=1&enablejsapi=1';
      /* ⚠️ NO autoplay=1 here, however tempting. Tried and measured: with autoplay in the URL
         YouTube begins playing the moment the iframe loads, and attaching the IFrame API a
         moment later interrupts it — so the video started on a SLOW connection (API wins the
         race) and failed on a fast one (playback wins, then gets cut off). Exactly backwards,
         and it fails for the visitors with the best connections.
         Playback is started explicitly from onReady instead, muted, which no browser refuses
         and which cannot race with the attach because it happens after it. */
    }

    /* SAIF_AUTOPLAY_V2 — a lesson playing silently looks broken, and 39% of the people it
       happened to never touched the page and so never heard a word of it. The document-level
       unmute-on-first-interaction stays, but it is invisible; this says out loud that there is
       sound waiting. Inline styles so the fix is one file. */
    function showSoundInvite(host, player, ctx) {
      if (!host || host.querySelector('.saif-sound-invite')) return;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'saif-sound-invite';
      b.setAttribute('aria-label',
        (window.SaifI18n && window.SaifI18n.lesson_tap_for_sound) || 'Turn the sound on');
      b.style.cssText = [
        'position:absolute', 'left:50%', 'bottom:14px', 'transform:translateX(-50%)',
        'z-index:3', 'display:inline-flex', 'align-items:center', 'gap:8px',
        'padding:10px 16px', 'border:0', 'border-radius:999px', 'cursor:pointer',
        'background:rgba(12,10,8,.82)', 'color:#fff',
        'font:600 14px/1 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif',
        '-webkit-backdrop-filter:blur(6px)', 'backdrop-filter:blur(6px)',
        'box-shadow:0 2px 14px rgba(0,0,0,.35)',
      ].join(';');
      b.innerHTML =
        '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/>' +
        '<path d="M19 5a9 9 0 0 1 0 14"/></svg>' +
        ((window.SaifI18n && window.SaifI18n.lesson_tap_for_sound) || 'Tap for sound');
      b.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        /* A real click, so this one is always allowed. */
        try { player.unMute(); player.playVideo(); } catch (err) {}
        b.remove();
        if (window.SaifTrack) window.SaifTrack('lesson_video_unmuted', Object.assign({ how: 'button' }, ctx || {}));
      });
      host.appendChild(b);
      /* If sound arrives another way — the document-level listener — take the badge away. */
      var watch = setInterval(function () {
        var m = true;
        try { m = player.isMuted(); } catch (err) {}
        if (!m) { clearInterval(watch); if (b.parentNode) b.remove(); }
      }, 700);
      setTimeout(function () { clearInterval(watch); }, 60000);
    }

    var iframe = document.createElement('iframe');
    iframe.src = videoUrl;
    /* ⚠️ Never lazy-load the video we are trying to autoplay. loading="lazy" defers creating
       the iframe until it nears the viewport, which on a phone put it behind the very deadline
       the old code was failing. It is the point of the page; load it. */
    iframe.loading = (opts && opts.autoplay) ? 'eager' : 'lazy';
    iframe.title = (window.SaifI18n && window.SaifI18n.lesson_video_player_title) || 'Lesson video player';
    iframe.allow = 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    container.innerHTML = '';
    container.appendChild(iframe);

    /* SAIF_END_MODAL_V1 + SAIF_YT_TELEMETRY */
    if (ytId) {
      loadYouTubeApi(function () {
        try {
          var ytFired = false;
          var lessonContext = (opts && opts.context) || {};
          var playFired = false;
          var marks = {};
          var poll = null;
          var ytPlayer = null;
          /* SAIF_AUTOPLAY_V2 — the verdict comes from onStateChange actually firing, with a
             generous backstop, rather than from guessing at 700ms. */
          var sawPlaying = false;
          var soundTried = false;
          var autoplayVerdict = null;

          var stopPoll = function () { if (poll) { clearInterval(poll); poll = null; } };

          var announceAutoplay = function (mode) {
            if (window.SaifTrack) {
              window.SaifTrack('lesson_video_autoplay',
                Object.assign({ result: mode, player: 'youtube' }, lessonContext));
            }
            if (mode === 'muted') {
              showSoundInvite(container, ytPlayer, lessonContext);
              if (opts && typeof opts.onMutedAutoplay === 'function') opts.onMutedAutoplay(ytPlayer);
            }
          };

          /* SAIF_AUTOPLAY_V2 — the video is playing muted; now try for sound.
             Unmuting without a gesture is allowed for visitors the browser already trusts
             (Chrome keeps a per-site media engagement score) and refused for everyone else —
             and a refusal PAUSES the video. So check, and put it back if that happened.
             Either way the lesson keeps playing, which the old code could not promise. */
          var askForSound = function () {
            try { ytPlayer.unMute(); } catch (err) {}
            setTimeout(function () {
              var st = -1, muted = true;
              try { st = ytPlayer.getPlayerState(); } catch (err) {}
              try { muted = ytPlayer.isMuted(); } catch (err) {}
              if (st === 1 && !muted) { announceAutoplay('unmuted'); return; }
              try { ytPlayer.mute(); if (st !== 1) ytPlayer.playVideo(); } catch (err) {}
              announceAutoplay('muted');
            }, 600);
          };

          /* YouTube has no timeupdate — poll for the same 25/50/75 marks the
             Cloudflare Stream branch reports, so both platforms are comparable. */
          var startPoll = function () {
            stopPoll();
            poll = setInterval(function () {
              if (!ytPlayer || !window.SaifTrack) return;
              var d = 0, t = 0;
              try { d = ytPlayer.getDuration() || 0; t = ytPlayer.getCurrentTime() || 0; } catch (err) { return; }
              if (!d) return;
              var pct = (t / d) * 100;
              [25, 50, 75].forEach(function (m) {
                if (pct >= m && !marks[m]) {
                  marks[m] = true;
                  window.SaifTrack('lesson_video_progress',
                    Object.assign({ milestone: m, percent: Math.round(pct), player: 'youtube' }, lessonContext));
                  saifAnnounceProgress(m);
                }
              });
            }, 2000);
          };

          ytPlayer = new window.YT.Player(iframe, {
            events: {
              onReady: function (e) {
                /* ⚠️ Use e.target, not the closure variable. onReady can fire before
                   `ytPlayer = new YT.Player(...)` has finished assigning, leaving it null —
                   and every call on it then throws straight into the catch below, so the
                   video silently never starts. Measured on desktop; the phone happened to
                   win the race and looked fine, which is how this hides. */
                if (e && e.target) ytPlayer = e.target;
                if (!(opts && opts.autoplay)) return;
                /* ⚠️ BELT AND BRACES, and the second brace is not optional.
                   The URL asks for autoplay, but that request is quietly ignored in some
                   browsers and whenever the IFrame API attaches to an existing iframe.
                   Relying on it ALONE stopped the video starting at all on a fast connection
                   — measured, not guessed, when this fix was first tried. So also ask
                   explicitly, MUTED, which is the one form of playback browsers always allow.
                   Sound is a separate question, handled once it is actually playing. */
                try { ytPlayer.mute(); ytPlayer.playVideo(); } catch (err) {}
                /* And a real backstop, so "blocked" means blocked rather than "slower than a
                   stopwatch" — which is what the previous 1.4-second verdict really meant. */
                autoplayVerdict = setTimeout(function () {
                  if (!sawPlaying) announceAutoplay('blocked');
                }, 8000);
              },
              onStateChange: function (e) {
                if (!e) return;
                if (e.target) ytPlayer = e.target;               /* same race as onReady */
                if (e.data === 1) {                              // 1 = PLAYING
                  /* SAIF_AUTOPLAY_V2 — it is playing, so the only open question is sound. */
                  sawPlaying = true;
                  if (autoplayVerdict) { clearTimeout(autoplayVerdict); autoplayVerdict = null; }
                  if (opts && opts.autoplay && !soundTried) { soundTried = true; askForSound(); }
                  if (!playFired) {
                    playFired = true;
                    if (window.SaifTrack) {
                      window.SaifTrack('lesson_video_started',
                        Object.assign({ player: 'youtube' }, lessonContext));
                    }
                  }
                  startPoll();
                  return;
                }
                if (e.data === 2) { stopPoll(); return; }        // 2 = PAUSED
                if (e.data !== 0 || ytFired) return;             // 0 = ENDED
                ytFired = true;
                stopPoll();
                if (opts && typeof opts.onEnded === 'function') opts.onEnded();
              }
            }
          });
        } catch (err) { /* video still plays; completion just cannot be detected */ }
      });
    }

    // For Cloudflare Stream embeds, lazy-load the Player SDK so we can
    // listen for `ended` (cross-origin iframes block direct event access).
    // Other providers (YouTube/Vimeo) keep the plain iframe path.
    var isStream = videoUrl.indexOf('cloudflarestream.com') !== -1
                || videoUrl.indexOf('iframe.videodelivery.net') !== -1;
    if (isStream) {
      loadStreamSdk(function () {
        try {
          var player = window.Stream(iframe);
          var lessonContext = (opts && opts.context) || {};
          var milestonesFired = {}; // {25: true, ...}
          var playStarted = false;

          player.addEventListener('play', function () {
            if (!playStarted && window.SaifTrack) {
              playStarted = true;
              window.SaifTrack('lesson_video_started', lessonContext);
            }
          });

          player.addEventListener('timeupdate', function () {
            if (!window.SaifTrack) return;
            var t = player.currentTime || 0;
            var d = player.duration || 0;
            if (!d) return;
            var pct = (t / d) * 100;
            [25, 50, 75].forEach(function (m) {
              if (pct >= m && !milestonesFired[m]) {
                milestonesFired[m] = true;
                window.SaifTrack('lesson_video_progress', Object.assign({ milestone: m, percent: Math.round(pct) }, lessonContext));
                saifAnnounceProgress(m);
              }
            });
          });

          if (opts && typeof opts.onEnded === 'function') {
            player.addEventListener('ended', opts.onEnded);
          }
          player.addEventListener('ended', function () {
            if (window.SaifTrack) window.SaifTrack('lesson_video_ended', lessonContext);
          });
        } catch (e) {
          // SDK failed — silently degrade to a plain iframe; the user
          // still gets the video, just no post-lesson CTA.
        }
      });
    }
    // NOTE (2026-07): there is deliberately no YouTube 'ended' handling here.
    // YouTube won't fire `ended` without the IFrame API, so this used to fall back to
    // a duration timer — and lesson-0's duration is empty, so it fell through again to
    // "reveal when the visitor hides the tab". That is what made the CTA appear on a
    // tab switch and then scroll-jack the page. The post-lesson block is now part of
    // the page (shown on load for non-purchasers), so nothing needs to trigger it.
  }

  // Count every lesson across the curriculum's modules.
  function countLessons(course) {
    var mods = (course && course.curriculum && course.curriculum.modules) || (course && course.modules) || [];
    var t = 0;
    for (var i = 0; i < mods.length; i++) t += ((mods[i] && mods[i].lessons) || []).length;
    return t;
  }
  // 1-based position of a lesson id in the flattened curriculum; 0 if not found.
  function lessonIndex(course, lessonId) {
    var mods = (course && course.curriculum && course.curriculum.modules) || (course && course.modules) || [];
    var k = 0;
    for (var i = 0; i < mods.length; i++) {
      var ls = (mods[i] && mods[i].lessons) || [];
      for (var j = 0; j < ls.length; j++) { k++; if (ls[j] && ls[j].id === lessonId) return k; }
    }
    return 0;
  }

  /* SAIF_LESSON_ASK_V1 — measured 2026-08-09, the first month the free lesson was live.

     228 people opened it. 149 got the video playing, 97 reached a quarter, 41 reached three
     quarters, and SIX watched to the end. The lesson's selling moment — the modal that says
     "enjoyed that, here is the rest" — fires on the video ending, so the best-placed pitch on
     the site had been shown to six people in a month.

     Worse, the permanent block under the video was rendered the instant the page loaded,
     reading "That was lesson 1 of 9. Enjoyed the lesson?" over a video sitting at three
     seconds of three minutes. It asked whether somebody enjoyed a thing they had not watched,
     and by the time they had, they had already scrolled past it.

     So the block now starts as a plain statement of what the course contains, and becomes the
     ask once they have actually watched three quarters — which is 41 people rather than 6. */
  var saifProgressListeners = [];
  function saifAnnounceProgress(milestone) {
    saifProgressListeners.forEach(function (fn) {
      try { fn(milestone); } catch (e) { /* one bad listener must not stop the others */ }
    });
  }

  function showEndCta(course, courseHandle, currentLessonId) {
    var cta = document.getElementById('SaifLessonEndCta');
    if (!cta) return;
    var enroll = document.getElementById('SaifLessonEndCtaEnroll');
    // Discount CTA removed (owner, July 2026) — no code exists.
    var titleEl = document.getElementById('SaifLessonEndCtaTitle');
    var descEl = document.getElementById('SaifLessonEndCtaDesc');
    var courseTitle = (course && course.title) ? course.title : courseHandle;
    var price = course && course.price ? course.price : '';
    var courseUrl = (course && course.url) ? course.url : saifPath('/products/' + courseHandle);

    /* Written before they have watched it, so it claims nothing about them. */
    var askedYet = false;
    if (titleEl) titleEl.textContent =
      (window.SaifI18n && window.SaifI18n.rest_of_course) || 'The rest of the course';
    if (descEl) {
      // Product titles read "Beginner Course: Foundation of Recitation" — the level
      // prefix is noise mid-sentence, so drop it the way the course cards do.
      var shortTitle = courseTitle.indexOf(':') !== -1
        ? courseTitle.split(':').pop().trim()
        : courseTitle;
      var unlockTpl = (window.SaifI18n && window.SaifI18n.unlock_rest) ||
        'Unlock the rest of {course} — every lesson, the Practice Studio, and personal feedback from Saif.';
      descEl.textContent = unlockTpl.split('{course}').join(shortTitle);
    }
    // "That was lesson 1 of 9" — position gives the free lesson a size, which a bare
    // "Enjoyed the lesson?" doesn't. Only shown when we can count the curriculum.
    var kickerEl = document.getElementById('SaifLessonEndCtaKicker');
    var total = countLessons(course);
    var idx = lessonIndex(course, currentLessonId);
    var canCount = total > 1 && idx > 0;
    if (kickerEl) {
      if (canCount) {
        /* "Lesson 1 of 9" is true on arrival. "That WAS lesson 1 of 9" is not. */
        var beforeTpl = (window.SaifI18n && window.SaifI18n.lesson_n_of_total) || 'Lesson {n} of {total}';
        kickerEl.textContent = beforeTpl.split('{n}').join(idx).split('{total}').join(total);
        kickerEl.hidden = false;
      } else {
        kickerEl.hidden = true;
      }
    }

    /* The ask, once they have watched enough to have an opinion. Fired by the three-quarter
       milestone from either player, or by the video ending for whoever gets that far. */
    var becomeTheAsk = function (why) {
      if (askedYet) return;
      askedYet = true;
      if (titleEl) titleEl.textContent =
        (window.SaifI18n && window.SaifI18n.enjoyed_lesson) || 'Enjoyed the lesson?';
      if (kickerEl && canCount) {
        var afterTpl = (window.SaifI18n && window.SaifI18n.lesson_x_of_y) || 'That was lesson {n} of {total}';
        kickerEl.textContent = afterTpl.split('{n}').join(idx).split('{total}').join(total);
      }
      if (window.SaifTrack) window.SaifTrack('lesson_end_cta_ready', {
        course_handle: courseHandle, lesson_id: currentLessonId, why: why
      });
    };
    saifProgressListeners.push(function (m) { if (m >= 75) becomeTheAsk('watched_75'); });
    document.addEventListener('saif:lesson-ended', function () { becomeTheAsk('ended'); });
    if (enroll) {
      enroll.href = courseUrl;
      enroll.textContent = price ? ((window.SaifI18n && window.SaifI18n.join_for) || 'Join for') + ' '+ price : (window.SaifI18n && window.SaifI18n.enroll_now) || 'Join now';
      /* ⚠️ This block has existed since launch with no tracking on it at all, so nobody could
         say whether it was being pressed and the course page lost people, or never pressed.
         `asked` records which of the two states they pressed it in. */
      if (!enroll.dataset.saifTracked) {
        enroll.dataset.saifTracked = '1';
        enroll.addEventListener('click', function () {
          if (window.SaifTrack) window.SaifTrack('lesson_end_cta_click', {
            course_handle: courseHandle, lesson_id: currentLessonId, asked: askedYet
          });
        });
      }
    }
    cta.hidden = false;
    // No scrollIntoView: the block sits in the normal page flow directly under the
    // video, so the reader reaches it by scrolling. Yanking the viewport was half of
    // why this felt broken.
  }


  /* SAIF_END_MODAL_V1 — loads YouTube's IFrame API once, queueing callers until ready.
     Adblockers commonly block this script; if that happens nothing below runs and the
     lesson plays exactly as it does today. */
  function loadYouTubeApi(callback) {
    if (window.YT && window.YT.Player) { callback(); return; }
    if (window.__saifYtQueue) { window.__saifYtQueue.push(callback); return; }
    window.__saifYtQueue = [callback];
    var prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof prev === 'function') { try { prev(); } catch (e) {} }
      var q = window.__saifYtQueue || [];
      window.__saifYtQueue = null;
      q.forEach(function (cb) { try { cb(); } catch (e) {} });
    };
    var s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.async = true;
    s.onerror = function () { window.__saifYtQueue = null; };
    document.head.appendChild(s);
  }

  /* Styles live here so this feature is contained in one file. */
  function ensureEndModalStyles() {
    if (document.getElementById('SaifEndModalStyles')) return;
    var css = document.createElement('style');
    css.id = 'SaifEndModalStyles';
    css.textContent =
      '.saif-endmodal{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;' +
      'justify-content:center;padding:2rem;background:rgba(26,24,20,.62);opacity:0;' +
      'transition:opacity .22s ease}' +
      '.saif-endmodal[data-open="1"]{opacity:1}' +
      '.saif-endmodal__card{background:#FFFCF6;border:1px solid #E8E3D9;border-radius:1.4rem;' +
      'max-width:44rem;width:100%;padding:3.2rem 3rem 2.8rem;text-align:center;position:relative;' +
      'box-shadow:0 24px 60px -20px rgba(0,0,0,.4);transform:translateY(10px) scale(.985);' +
      'transition:transform .22s ease}' +
      '.saif-endmodal[data-open="1"] .saif-endmodal__card{transform:none}' +
      '.saif-endmodal__kicker{font-family:inherit;font-size:1.2rem;letter-spacing:.14em;' +
      'text-transform:uppercase;font-weight:600;color:var(--saif-accent-btn, #8A6508);margin:0 0 1.2rem}' +
      '.saif-endmodal__title{font-family:inherit;font-size:2.6rem;line-height:1.2;font-weight:600;' +
      'color:#1A1814;margin:0 0 1rem}' +
      '.saif-endmodal__desc{font-family:inherit;font-size:1.5rem;line-height:1.6;color:#4A453E;' +
      'margin:0 0 2.4rem}' +
      '.saif-endmodal__cta{display:inline-block;background:#B8860B;color:#fff;font-size:1.5rem;' +
      'font-weight:600;letter-spacing:.04em;text-transform:uppercase;text-decoration:none;' +
      'padding:1.5rem 3.4rem;border-radius:.5rem;transition:background .18s}' +
      '.saif-endmodal__cta:hover{background:#D4A84B;color:#fff}' +
      /* SAIF_MODAL_PRICE_V1 */
      '.saif-endmodal__was{text-decoration:line-through;opacity:.62;font-weight:500;' +
      'margin-right:.5em;text-decoration-thickness:.12em}' +
      '.saif-endmodal__reward{margin:1.4rem 0 0;font-family:inherit;font-size:1.35rem;' +
      'line-height:1.5;color:#2D6A5A;font-weight:600}' +
      '.saif-endmodal__later{display:block;margin:1.6rem auto 0;background:none;border:0;' +
      'font-family:inherit;font-size:1.3rem;color:#6B6560;cursor:pointer;text-decoration:underline}' +
      '.saif-endmodal__close{position:absolute;top:1rem;right:1.2rem;background:none;border:0;' +
      'font-size:2.4rem;line-height:1;color:#8A8480;cursor:pointer;padding:.4rem .8rem}' +
      '.saif-endmodal__cta:focus-visible,.saif-endmodal__later:focus-visible,' +
      '.saif-endmodal__close:focus-visible{outline:2px solid #B8860B;outline-offset:3px}' +
      '@media(max-width:600px){.saif-endmodal{padding:1.2rem}' +
      '.saif-endmodal__card{padding:2.6rem 1.8rem 2.2rem}.saif-endmodal__title{font-size:2.2rem}}' +
      '@media(prefers-reduced-motion:reduce){.saif-endmodal,.saif-endmodal__card{transition:none}}';
    document.head.appendChild(css);
  }

  /* SAIF_MODAL_PRICE_V1 — render `cents` using the separators and symbol placement of an
     already-formatted price from the same market. The store runs 237 markets, so rather than
     encode every convention this copies the shape of a string Shopify already produced. */
  function saifRenderLike(formatted, cents) {
    try {
      if (!formatted || typeof cents !== 'number' || !isFinite(cents)) return null;
      var str = String(formatted);
      var m = str.match(/\d[\d.,\u00a0\u202f' ]*\d|\d/);
      if (!m) return null;
      var token = m[0];
      /* two trailing digits behind a . or , means this market shows minor units */
      var decM = token.match(/([.,])(\d{2})$/);
      var decSep = decM ? decM[1] : '';
      var whole = decSep ? token.slice(0, -3) : token;
      var grpM = whole.match(/[.,\u00a0\u202f' ]/);
      var grpSep = grpM ? grpM[0] : '';
      var wholePart = decSep ? Math.floor(cents / 100) : Math.round(cents / 100);
      var fracPart = decSep ? String(cents % 100).padStart(2, '0') : '';
      var ws = String(wholePart);
      if (grpSep) ws = ws.replace(/\B(?=(\d{3})+(?!\d))/g, grpSep);
      return str.replace(token, ws + (decSep ? decSep + fracPart : ''));
    } catch (e) { return null; }
  }

  /* SAIF_MODAL_PRICE_V1 — the safety net. Before trusting the renderer with a NEW amount, make
     it reproduce the ORIGINAL string from the original amount. If it cannot — Indian lakh
     grouping, a currency with three decimals, anything unforeseen — return null and the caller
     leaves the plain price untouched. A market we format wrongly shows no discount rather than
     a wrong number. */
  function saifDiscountedLabel(formatted, origCents, newCents) {
    if (saifRenderLike(formatted, origCents) !== String(formatted)) return null;
    var out = saifRenderLike(formatted, newCents);
    return out === String(formatted) ? null : out;
  }

  /* Opens once per lesson view. Reuses the strings the inline block already renders so the
     copy and its translations stay in one place. */
  function showEndModal(course, courseHandle, currentLessonId, lessonContext) {
    if (window.__saifEndModalShown === currentLessonId) return;
    window.__saifEndModalShown = currentLessonId;
    ensureEndModalStyles();

    var courseTitle = (course && course.title) ? course.title : courseHandle;
    var shortTitle = courseTitle.indexOf(':') !== -1 ? courseTitle.split(':').pop().trim() : courseTitle;
    var price = course && course.price ? course.price : '';
    var courseUrl = (course && course.url) ? course.url : saifPath('/products/' + courseHandle);
    var total = countLessons(course);
    var idx = lessonIndex(course, currentLessonId);

    var wrap = document.createElement('div');
    wrap.className = 'saif-endmodal';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'SaifEndModalTitle');

    var kicker = '';
    if (total > 1 && idx > 0) {
      var kt = (window.SaifI18n && window.SaifI18n.lesson_x_of_y) || 'That was lesson {n} of {total}';
      kicker = '<p class="saif-endmodal__kicker">' + kt.split('{n}').join(idx).split('{total}').join(total) + '</p>';
    }
    var title = (window.SaifI18n && window.SaifI18n.enjoyed_lesson) || 'Enjoyed the lesson?';
    var unlockTpl = (window.SaifI18n && window.SaifI18n.unlock_rest) ||
      'Unlock the rest of {course} — every lesson, the Practice Studio, and personal feedback from Saif.';
    var desc = unlockTpl.split('{course}').join(shortTitle);
    var join = price ? ((window.SaifI18n && window.SaifI18n.join_for) || 'Join for') + ' '+ price : ((window.SaifI18n && window.SaifI18n.enroll_now) || 'Join now');

    var card = document.createElement('div');
    card.className = 'saif-endmodal__card';
    card.innerHTML =
      '<button type="button" class="saif-endmodal__close" aria-label="Close">&times;</button>' +
      kicker +
      '<h2 class="saif-endmodal__title" id="SaifEndModalTitle"></h2>' +
      '<p class="saif-endmodal__desc"></p>' +
      '<a class="saif-endmodal__cta"></a>' +
      '<p class="saif-endmodal__reward" hidden></p>' +
      '<button type="button" class="saif-endmodal__later">Keep looking around</button>';
    card.querySelector('.saif-endmodal__title').textContent = title;
    card.querySelector('.saif-endmodal__desc').textContent = desc;
    var cta = card.querySelector('.saif-endmodal__cta');
    cta.href = courseUrl;
    cta.textContent = join;

    /* SAIF_END_MODAL_CART_V1 — upgrade the button from "go to the product page" to
       "cart, loaded, discount applied". Resolved now rather than on click so the button is
       ready when pressed. Any failure leaves courseUrl in place. */
    /* SAIF_END_MODAL_NO_DISCOUNT_V1 (owner, 2026-08-04) — the 20% offer is OFF.

       The free lesson is not a teaser, it is Module 0 of the course. So a buyer has a real
       reason to open it, and the moment they do they are shown a discount they can no longer
       use. The `purchased` gate above only closes that door for someone who is SIGNED IN —
       the lesson lives on a public URL, and a buyer arriving logged out (new device, guest
       checkout, entitlement not yet resolved) reads `purchased: false` and gets the offer.
       Being told about 20% you have already missed is worse than never seeing it.

       Empty string rather than deleting the machinery: every branch below is already guarded
       on this constant, so the modal falls back to "Join for $59.99" with a plain cart link,
       no struck price and no reward line. Re-enabling is one string. */
    var END_MODAL_DISCOUNT = '';
    (function upgradeToCartLink() {
      try {
        fetch(saifPath('/products/' + courseHandle + '.js'), { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            if (!p || !p.variants || !p.variants.length) return;
            var v = null;
            for (var i = 0; i < p.variants.length; i++) {
              if (p.variants[i].available) { v = p.variants[i]; break; }
            }
            if (!v) return;
            /* storefront=true is REQUIRED — without it Shopify sends the buyer to the
               checkout login wall instead of the cart. */
            var url = saifPath('/cart/' + v.id + ':1') + '?storefront=true';
            if (END_MODAL_DISCOUNT) url += '&discount=' + encodeURIComponent(END_MODAL_DISCOUNT);
            cta.href = url;
            /* SAIF_MODAL_PRICE_V1 — now that the real amount is known, show what they pay.
               v.price is an integer in minor units, so the 20% is taken on the number and
               only then formatted. If anything is missing the plain label simply stays. */
            if (END_MODAL_DISCOUNT && price && typeof v.price === 'number') {
              var saved = saifDiscountedLabel(price, v.price, Math.round(v.price * 0.8));
              if (saved) {
                var wasEl = document.createElement('s');
                wasEl.className = 'saif-endmodal__was';
                wasEl.textContent = price;
                cta.textContent = '';
                cta.appendChild(document.createTextNode(
                  ((window.SaifI18n && window.SaifI18n.join_for) || 'Join for') + ' '));
                cta.appendChild(wasEl);
                cta.appendChild(document.createTextNode(saved));
                /* screen readers should hear the price they pay, not two numbers in a row */
                cta.setAttribute('aria-label', 'Join for ' + saved + ', reduced from ' + price);
              }
            }
            if (END_MODAL_DISCOUNT) {
              var rewardEl = card.querySelector('.saif-endmodal__reward');
              if (rewardEl) {
                /* ⚠️ Scoped to PAY IN FULL (owner, 2026-08-03). A promo code does not apply to a
                   payment-plan line — measured: SAIF20 takes 20% off a pay-in-full course and
                   exactly 0% off the same course on a plan. Promising it unconditionally meant
                   someone quoted $47.99 who then chose instalments paid $79.96 and was told
                   nothing. The cart now says so in red too. (Also loses an em dash.) */
                rewardEl.textContent = (window.SaifI18n && window.SaifI18n.watched_reward) ||
                  'You watched the whole lesson. 20% off is applied at the cart when you pay in full.';
                rewardEl.hidden = false;
              }
            }
          })
          .catch(function () { /* keep the product-page link */ });
      } catch (e) { /* keep the product-page link */ }
    })();
    wrap.appendChild(card);
    document.body.appendChild(wrap);

    var lastFocus = document.activeElement;
    var prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { wrap.setAttribute('data-open', '1'); });
    cta.focus();

    function close() {
      wrap.removeAttribute('data-open');
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      setTimeout(function () { if (wrap.parentNode) wrap.parentNode.removeChild(wrap); }, 220);
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    wrap.addEventListener('click', function (e) { if (e.target === wrap) close(); });
    card.querySelector('.saif-endmodal__close').addEventListener('click', close);
    card.querySelector('.saif-endmodal__later').addEventListener('click', close);
    cta.addEventListener('click', function () {
      if (window.SaifTrack) window.SaifTrack('lesson_end_modal_cta', lessonContext || {});
    });
    if (window.SaifTrack) window.SaifTrack('lesson_video_completed', lessonContext || {});
  }

  function loadStreamSdk(callback) {
    if (window.Stream) { callback(); return; }
    if (window.__streamSdkLoading) {
      window.__streamSdkLoading.push(callback);
      return;
    }
    window.__streamSdkLoading = [callback];
    var s = document.createElement('script');
    s.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
    s.async = true;
    s.onload = function () {
      var queue = window.__streamSdkLoading || [];
      window.__streamSdkLoading = null;
      queue.forEach(function (cb) { try { cb(); } catch (e) {} });
    };
    s.onerror = function () { window.__streamSdkLoading = null; };
    document.head.appendChild(s);
  }

  /* SAIF_LOCK_V2 — measured 2026-08-09, first month of the course being live.

     15 people opened a lesson they had not bought. 12 of them hit it on lesson 1, immediately
     after finishing the free lesson and pressing next. Only 4 of the 15 ever reached a course
     page afterwards, none reached the cart, and four people opened several locked lessons in a
     row, one of them eight, clicking round the sidebar hoping something would give.

     The card told them "Enroll to unlock every lesson" without ever saying what those lessons
     are. It now names them. And the button is tracked, because until now we could see that 15
     people looked at this and nothing followed, but not whether anybody pressed it, which are
     two different problems with two different fixes. */
  function renderLockedLesson(lesson, course, courseHandle, container, allLessons) {
    if (!container) return;
    container.classList.add('saif-lesson__video--empty');
    var price = course && course.price ? course.price : '';
    var courseUrl = (course && course.url) ? course.url : saifPath('/products/' + courseHandle);
    var courseTitle = (course && course.title) ? course.title : courseHandle;

    // Pull optional rich-state config (instructor avatar/name + testimonial)
    // from the lesson section. All fields gracefully degrade when missing
    // — the card simply renders a smaller, simpler version.
    var lockedCfg = readJSON('SaifLockedLessonConfig') || {};
    var hasInstructor = !!(lockedCfg.instructorName || lockedCfg.instructorAvatar);
    var hasTestimonial = !!lockedCfg.testimonialQuote;
    var isRich = hasInstructor || hasTestimonial;

    var html = '<div class="saif-lesson__locked' + (isRich ? ' saif-lesson__locked--rich' : '') + '">';

    if (hasInstructor) {
      html += '<div class="saif-lesson__locked-instructor">';
      if (lockedCfg.instructorAvatar) {
        html += '<img class="saif-lesson__locked-avatar" src="' + escapeHtml(lockedCfg.instructorAvatar) + '" alt="" width="48" height="48" loading="lazy">';
      }
      if (lockedCfg.instructorName) {
        html += '<span class="saif-lesson__locked-instructor-name">' + escapeHtml(lockedCfg.instructorName) + '</span>';
      }
      html += '</div>';
    } else {
      html += '<div class="saif-lesson__locked-icon" aria-hidden="true">' +
        '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' +
        '</div>';
    }

    /* Everything still locked, this lesson included, so the count is what enrolling buys. */
    var lockedList = [];
    for (var li = 0; li < (allLessons || []).length; li++) {
      if (allLessons[li].accessible === false) lockedList.push(allLessons[li]);
    }
    var preview = [];
    for (var pi = 0; pi < lockedList.length && preview.length < 3; pi++) {
      if (lockedList[pi].id !== lesson.id) preview.push(lockedList[pi]);
    }
    var remaining = lockedList.length - preview.length - 1;

    html += '<h2 class="saif-lesson__locked-title">Unlock the full course</h2>';

    if (lockedList.length > 1 && preview.length) {
      html += '<p class="saif-lesson__locked-desc">' +
        '<strong>' + lockedList.length + ' more lesson' + (lockedList.length === 1 ? '' : 's') +
        '</strong> in ' + escapeHtml(courseTitle) + ', starting with this one:</p>' +
        '<ul class="saif-lesson__locked-list">' +
          '<li><strong>' + escapeHtml(lesson.title) + '</strong></li>';
      for (var vi = 0; vi < preview.length; vi++) {
        html += '<li>' + escapeHtml(preview[vi].title) + '</li>';
      }
      html += '</ul>';
      if (remaining > 0) {
        html += '<p class="saif-lesson__locked-more">and ' + remaining + ' more</p>';
      }
    } else {
      /* One locked lesson, or a curriculum we could not read: the original wording, which is
         still true. */
      html += '<p class="saif-lesson__locked-desc">' +
        escapeHtml(lesson.title) + ' is part of <strong>' + escapeHtml(courseTitle) + '</strong>. Enroll to unlock every lesson.' +
      '</p>';
    }

    if (hasTestimonial) {
      html += '<figure class="saif-lesson__locked-testimonial">' +
        '<blockquote>&ldquo;' + escapeHtml(lockedCfg.testimonialQuote) + '&rdquo;</blockquote>';
      if (lockedCfg.testimonialName) {
        html += '<figcaption>— ' + escapeHtml(lockedCfg.testimonialName) + '</figcaption>';
      }
      html += '</figure>';
    }

    html += '<a href="' + escapeHtml(courseUrl) + '" class="saif-lesson__locked-cta" data-saif-locked-cta="1">' +
        'Enroll' + (price ? ' — ' + escapeHtml(price) : '') +
      '</a>' +
      '<p class="saif-lesson__locked-guarantee">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>' +
        ' Lifetime access &mdash; yours forever' +
      '</p>' +
    '</div>';

    container.innerHTML = html;

    /* Without this the funnel stops dead at "saw the lock". A press means the card worked and
       the course page lost them; no press means the card is the problem. */
    var lockCta = container.querySelector('[data-saif-locked-cta]');
    if (lockCta) {
      lockCta.addEventListener('click', function () {
        if (window.SaifTrack) window.SaifTrack('lesson_locked_cta', {
          course_handle: courseHandle,
          lesson_id: lesson.id,
          locked_lessons: lockedList.length
        });
      });
    }
  }

  function renderSidebar(curriculum, currentLessonId, courseHandle, customerId) {
    var sidebar = document.getElementById('SaifLessonSidebarContent');
    if (!sidebar || !curriculum || !curriculum.modules) return;

    // Count total lessons and completed
    var totalLessons = 0;
    var completedCount = 0;
    curriculum.modules.forEach(function (mod) {
      if (mod.lessons) {
        totalLessons += mod.lessons.length;
        mod.lessons.forEach(function (l) {
          if (SaifProgress.isCompleted(customerId, courseHandle, l.id)) completedCount++;
        });
      }
    });
    var pct = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

    var html = '';
    html += '<div class="saif-lesson__sidebar-progress">';
    html += '<div class="saif-lesson__sidebar-progress-bar" role="progressbar" aria-valuenow="' + pct + '" aria-valuemin="0" aria-valuemax="100">';
    html += '<div class="saif-lesson__sidebar-progress-fill" style="width:' + pct + '%"></div>';
    html += '</div>';
    html += '<span class="saif-lesson__sidebar-progress-text">' + pct + '% complete</span>';
    html += '</div>';

    curriculum.modules.forEach(function (mod) {
      html += '<div class="saif-lesson__sidebar-module">';
      html += '<div class="saif-lesson__sidebar-module-title">' + escapeHtml(mod.title) + '</div>';

      if (mod.lessons) {
        mod.lessons.forEach(function (lesson) {
          var isActive = lesson.id === currentLessonId;
          var isCompleted = SaifProgress.isCompleted(customerId, courseHandle, lesson.id);
          var isLocked = lesson.accessible === false;
          var classes = 'saif-lesson__sidebar-lesson';
          if (isActive) classes += ' saif-lesson__sidebar-lesson--active';
          if (isCompleted) classes += ' saif-lesson__sidebar-lesson--completed';
          if (isLocked) classes += ' saif-lesson__sidebar-lesson--locked';

          html += '<a href="' + saifPath('/pages/lesson?course=' + courseHandle + '&lesson=' + lesson.id) + '" class="' + classes + '">';
          html += '<span class="saif-lesson__sidebar-lesson-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">';

          if (isCompleted) {
            html += '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>';
          } else if (isLocked) {
            html += '<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>';
          } else {
            html += '<circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>';
          }

          html += '</svg></span>';
          html += '<span>' + escapeHtml(lesson.title) + '</span>';
          if (lesson.duration) {
            html += '<span class="saif-lesson__sidebar-lesson-duration">' + escapeHtml(lesson.duration) + '</span>';
          }
          html += '</a>';
        });
      }

      html += '</div>';
    });

    sidebar.innerHTML = html;
  }

  function renderLessonNav(allLessons, currentIndex, courseHandle) {
    var prevBtn = document.getElementById('SaifLessonPrev');
    var nextBtn = document.getElementById('SaifLessonNext');

    if (prevBtn) {
      if (currentIndex > 0) {
        prevBtn.href = saifPath('/pages/lesson?course=' + courseHandle + '&lesson=' + allLessons[currentIndex - 1].id);
        prevBtn.hidden = false;
      } else {
        prevBtn.hidden = true;
      }
    }

    if (nextBtn) {
      if (currentIndex < allLessons.length - 1) {
        nextBtn.href = saifPath('/pages/lesson?course=' + courseHandle + '&lesson=' + allLessons[currentIndex + 1].id);
        nextBtn.hidden = false;
      } else {
        nextBtn.hidden = true;
      }
    }
  }

  function bindCompleteButton(customerId, courseHandle, lessonId, config) {
    var btn = document.getElementById('SaifLessonCompleteBtn');
    if (!btn) return;

    var isCompleted = SaifProgress.isCompleted(customerId, courseHandle, lessonId);
    updateCompleteButton(btn, isCompleted, config);

    btn.addEventListener('click', function () {
      var nowCompleted = SaifProgress.toggleComplete(customerId, courseHandle, lessonId);
      updateCompleteButton(btn, nowCompleted, config);
      // Re-render sidebar to update checkmarks
      var purchases = readJSON('SaifCoursePurchases');
      var courseData = readJSON('SaifCourseData');
      if (courseData && courseData[courseHandle]) {
        var curriculum = typeof courseData[courseHandle].curriculum === 'string'
          ? JSON.parse(courseData[courseHandle].curriculum)
          : courseData[courseHandle].curriculum;
        renderSidebar(curriculum, lessonId, courseHandle, customerId);

        // Check for course completion
        if (nowCompleted) {
          var pct = SaifProgress.getPercentage(customerId, courseHandle, (courseData[courseHandle].lessonCount || flattenLessons(curriculum).length));
          if (pct >= 100) {
            showCertificate(courseData[courseHandle].title || courseHandle, customerId);
          }
        }
      }
    });
  }

  function updateCompleteButton(btn, isCompleted, config) {
    btn.textContent = isCompleted ? config.completedText : config.completeText;
    btn.classList.toggle('saif-lesson__complete-btn--done', isCompleted);
  }

  function bindMobileSidebar() {
    var sidebar = document.getElementById('SaifLessonSidebar');
    var overlay = document.getElementById('SaifLessonOverlay');
    var menuBtn = document.getElementById('SaifLessonMenuBtn');
    var closeBtn = document.getElementById('SaifLessonSidebarClose');

    if (!sidebar || !overlay || !menuBtn) return;

    function openSidebar() {
      sidebar.classList.add('saif-lesson__sidebar--open');
      overlay.classList.add('saif-lesson__sidebar-overlay--visible');
      sidebar.setAttribute('aria-hidden', 'false');
      // Focus the first link in sidebar
      var firstLink = sidebar.querySelector('a');
      if (firstLink) firstLink.focus();
    }

    function closeSidebar() {
      sidebar.classList.remove('saif-lesson__sidebar--open');
      overlay.classList.remove('saif-lesson__sidebar-overlay--visible');
      sidebar.setAttribute('aria-hidden', 'true');
      menuBtn.focus();
    }

    menuBtn.addEventListener('click', openSidebar);
    overlay.addEventListener('click', closeSidebar);
    if (closeBtn) closeBtn.addEventListener('click', closeSidebar);

    // Close sidebar on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sidebar.classList.contains('saif-lesson__sidebar--open')) {
        closeSidebar();
      }
    });
  }

  /* ============================================
     MY COURSES DASHBOARD
     ============================================ */
  function initDashboard() {
    // My-courses section also uses the section.id suffix for this element.
    var customer = readJSONByPrefix('SaifCourseCustomer');
    if (!customer || !customer.loggedIn) return;

    var cards = document.querySelectorAll('[data-course-handle]');
    if (cards.length === 0) return;

    function renderCard(card) {
      var handle = card.getAttribute('data-course-handle');
      var totalLessons = parseInt(card.getAttribute('data-total-lessons')) || 0;

      // If we genuinely can't determine a lesson count, a "0%" bar is
      // misleading (looks broken / like the student has done nothing). Hide the
      // bar entirely rather than show a meaningless zero.
      if (totalLessons <= 0) {
        var emptyProgress = card.querySelector('.saif-course-card__progress');
        if (emptyProgress) emptyProgress.style.display = 'none';
        return;
      }

      var pct = SaifProgress.getPercentage(customer.customerId, handle, totalLessons);
      var bar = card.querySelector('.saif-course-card__progress-fill');
      var label = card.querySelector('.saif-course-card__progress-text');

      if (bar) {
        bar.style.width = pct + '%';
        var progressBar = card.querySelector('[role="progressbar"]');
        if (progressBar) progressBar.setAttribute('aria-valuenow', pct);
      }
      if (label) label.textContent = pct + '%';

      // Update continue link to last lesson
      var data = SaifProgress.get(customer.customerId, handle);
      var continueLink = card.querySelector('.saif-my-courses__continue-link');
      if (continueLink && data.lastLesson) {
        continueLink.href = saifPath('/pages/lesson?course=' + handle + '&lesson=' + data.lastLesson);
      }
    }

    // First pass from localStorage so the bars paint immediately.
    cards.forEach(renderCard);

    // On a fresh device localStorage is empty but D1 holds the real progress.
    // Backfill each enrolled course from the backend, then re-render so the
    // bars reflect actual completions instead of a misleading 0%.
    if (window.SaifSync && window.SaifSync.ready) {
      var handles = [];
      cards.forEach(function (card) {
        var h = card.getAttribute('data-course-handle');
        if (h && handles.indexOf(h) === -1) handles.push(h);
      });
      Promise.all(handles.map(function (h) {
        return SaifProgress.hydrateFromBackend(customer.customerId, h);
      })).then(function () {
        cards.forEach(renderCard);
      }).catch(function () { /* keep the localStorage pass on failure */ });
    }
  }

  /* ============================================
     COURSE DETAIL — PROGRESS CHECKMARKS
     ============================================ */
  function initCourseDetail() {
    var customer = readJSONByPrefix('SaifCourseCustomer');
    if (!customer || !customer.loggedIn) return;

    var courseEl = document.querySelector('[data-course-handle]');
    if (!courseEl) return;

    var courseHandle = courseEl.getAttribute('data-course-handle');
    var progressData = SaifProgress.get(customer.customerId, courseHandle);

    // Mark completed lessons in curriculum
    document.querySelectorAll('[data-lesson-id]').forEach(function (el) {
      var lessonId = el.getAttribute('data-lesson-id');
      if (progressData.completedLessons.indexOf(lessonId) !== -1) {
        el.classList.add('saif-course__lesson--completed');
      }
    });

    // Update hero progress bar
    var heroProgress = document.querySelector('.saif-course__hero-progress');
    if (heroProgress) {
      var totalLessons = parseInt(heroProgress.getAttribute('data-total-lessons')) || 0;
      if (totalLessons > 0) {
        var pct = SaifProgress.getPercentage(customer.customerId, courseHandle, totalLessons);
        var fill = heroProgress.querySelector('.saif-course__hero-progress-fill');
        var text = heroProgress.querySelector('.saif-course__hero-progress-text');
        var bar = heroProgress.querySelector('[role="progressbar"]');
        if (fill) fill.style.width = pct + '%';
        if (text) text.textContent = pct + '% complete';
        if (bar) bar.setAttribute('aria-valuenow', pct);
      }

      // Update continue link to last viewed lesson
      var continueBtn = document.getElementById('SaifCourseDetailContinue');
      if (continueBtn && progressData.lastLesson) {
        continueBtn.href = saifPath('/pages/lesson?course=' + courseHandle + '&lesson=' + progressData.lastLesson);
      }
    }
  }

  /* ============================================
     MODAL ACCESSIBILITY HELPER
     ============================================ */
  // (REQ-MODAL-001/002): saves the invoking element, moves focus into the
  // modal, traps Tab within it, closes on Escape, and restores focus on
  // close. Returns a release function — call it when the modal closes.
  function saifTrapModal(overlay, closeFn) {
    var previouslyFocused = document.activeElement;
    var selector = 'a[href], button:not([disabled]), input:not([disabled]), ' +
      'textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function focusables() {
      return Array.prototype.slice.call(overlay.querySelectorAll(selector));
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        closeFn();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !overlay.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown, true);

    var initial = focusables()[0];
    if (initial) { try { initial.focus(); } catch (e) { /* ignore */ } }

    return function () {
      document.removeEventListener('keydown', onKeydown, true);
      if (previouslyFocused && previouslyFocused.focus) {
        try { previouslyFocused.focus(); } catch (e) { /* ignore */ }
      }
    };
  }

  /* ============================================
     CERTIFICATE
     ============================================ */
  function showCertificate(courseTitle, customerId) {
    // Don't show twice per session
    if (document.getElementById('SaifCertificateModal')) return;

    var today = new Date();
    var dateStr = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    var modal = document.createElement('div');
    modal.id = 'SaifCertificateModal';
    modal.className = 'saif-certificate-modal';
    modal.innerHTML =
      '<div class="saif-certificate-modal__overlay"></div>' +
      '<div class="saif-certificate-modal__content" role="dialog" aria-modal="true" aria-label="Course completion certificate">' +
        '<button class="saif-certificate-modal__close" aria-label="Close">&times;</button>' +
        '<div class="saif-certificate" id="SaifCertificate">' +
          '<div class="saif-certificate__border">' +
            '<div class="saif-certificate__inner">' +
              '<p class="saif-certificate__label">Certificate of Completion</p>' +
              '<h2 class="saif-certificate__title">' + escapeHtml(courseTitle) + '</h2>' +
              '<div class="saif-certificate__divider"></div>' +
              '<p class="saif-certificate__text">This certifies that you have successfully completed all lessons in this course.</p>' +
              '<p class="saif-certificate__date">' + dateStr + '</p>' +
              '<div class="saif-certificate__footer">' +
                '<div class="saif-certificate__signature">' +
                  '<div class="saif-certificate__sig-line"></div>' +
                  '<p>Saif Al-Hussaini</p>' +
                  '<p class="saif-certificate__sig-title">Instructor</p>' +
                '</div>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="saif-certificate-modal__actions">' +
          '<button class="saif-certificate-modal__btn" id="SaifCertificateDownload">Download Certificate</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(modal);

    // Close handlers (Escape + focus trap + focus restore via saifTrapModal)
    var closeModal = function () {
      releaseTrap();
      modal.remove();
    };
    var releaseTrap = saifTrapModal(modal, closeModal);
    modal.querySelector('.saif-certificate-modal__close').addEventListener('click', closeModal);
    modal.querySelector('.saif-certificate-modal__overlay').addEventListener('click', closeModal);

    // Download as image using canvas
    document.getElementById('SaifCertificateDownload').addEventListener('click', function () {
      var cert = document.getElementById('SaifCertificate');
      // Simple: open print dialog for the certificate
      var printWin = window.open('', '_blank');
      printWin.document.write(
        '<html><head><title>Certificate — ' + escapeHtml(courseTitle) + '</title>' +
        '<style>' +
          'body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; font-family: Georgia, serif; }' +
          '.cert { width: 800px; padding: 60px; background: #fff; border: 3px double #B8860B; text-align: center; }' +
          '.cert h1 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.3em; color: #B8860B; margin: 0 0 20px; }' +
          '.cert h2 { font-size: 28px; margin: 0 0 20px; color: #1A1814; }' +
          '.cert .divider { width: 80px; height: 2px; background: #B8860B; margin: 20px auto; }' +
          '.cert p { font-size: 16px; color: #3D3B2F; margin: 10px 0; }' +
          '.cert .sig { margin-top: 40px; }' +
          '.cert .sig-line { width: 200px; height: 1px; background: #1A1814; margin: 0 auto 8px; }' +
          '.cert .sig-title { font-size: 13px; color: #666; }' +
        '</style></head><body>' +
        '<div class="cert">' +
          '<h1>Certificate of Completion</h1>' +
          '<h2>' + escapeHtml(courseTitle) + '</h2>' +
          '<div class="divider"></div>' +
          '<p>This certifies that you have successfully completed all lessons in this course.</p>' +
          '<p>' + dateStr + '</p>' +
          '<div class="sig">' +
            '<div class="sig-line"></div>' +
            '<p>Saif Al-Hussaini</p>' +
            '<p class="sig-title">Instructor</p>' +
          '</div>' +
        '</div>' +
        '<script>setTimeout(function(){window.print()},500)</script>' +
        '</body></html>'
      );
      printWin.document.close();
    });
  }

  /* ============================================
     LAST ACCESSED DATES
     ============================================ */
  function initLastAccessed() {
    document.querySelectorAll('[data-last-accessed]').forEach(function(el) {
      var handle = el.getAttribute('data-last-accessed');
      var customerEl = document.querySelector('[id^="SaifCourseCustomer"]');
      if (!customerEl) return;
      try {
        var data = JSON.parse(customerEl.textContent);
        if (!data.customerId) return;
        var key = 'saif_progress_' + data.customerId + '_' + handle;
        var stored = localStorage.getItem(key);
        if (stored) {
          var progress = JSON.parse(stored);
          if (progress.lastAccessed) {
            var date = new Date(progress.lastAccessed);
            var now = new Date();
            var diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
            var text = diff === 0 ? 'Last accessed today' : diff === 1 ? 'Last accessed yesterday' : 'Last accessed ' + diff + ' days ago';
            el.textContent = text;
          }
        }
      } catch(e) {}
    });
  }

  /* ============================================
     NOTES CHARACTER COUNT
     ============================================ */
  function initNotesCount() {
    var input = document.getElementById('SaifLessonNotesInput');
    var count = document.getElementById('SaifLessonNotesCount');
    if (!input || !count) return;
    function update() {
      count.textContent = String(input.value.length);
    }
    input.addEventListener('input', update);
    update();
  }

  /* ============================================
     TIME REMAINING ESTIMATE
     ============================================ */
  function initTimeRemaining() {
    var container = document.getElementById('SaifLessonTimeRemaining');
    var textEl = document.getElementById('SaifLessonTimeText');
    if (!container || !textEl) return;

    // Wait for sidebar to render with lesson data
    setTimeout(function() {
      var lessons = document.querySelectorAll('.saif-lesson__sidebar-lesson');
      if (!lessons.length) return;

      var totalMinutes = 0;
      var completedMinutes = 0;
      lessons.forEach(function(lesson) {
        var durationEl = lesson.querySelector('.saif-lesson__sidebar-duration');
        if (!durationEl) return;
        var match = durationEl.textContent.match(/(\d+)/);
        if (!match) return;
        var mins = parseInt(match[1]);
        totalMinutes += mins;
        if (lesson.classList.contains('saif-lesson__sidebar-lesson--completed')) {
          completedMinutes += mins;
        }
      });

      var remaining = totalMinutes - completedMinutes;
      if (remaining > 0) {
        var hours = Math.floor(remaining / 60);
        var mins = remaining % 60;
        var text = hours > 0 ? '~' + hours + 'h ' + mins + 'min remaining' : '~' + mins + 'min remaining';
        textEl.textContent = text;
        container.hidden = false;
      }
    }, 1000);
  }

  /* ============================================
     PRACTICE STREAK TRACKER
     ============================================ */
  var SaifStreak = {
    _key: function (cid) { return 'saif_streak_' + cid; },

    get: function (cid) {
      try {
        var d = localStorage.getItem(this._key(cid));
        return d ? JSON.parse(d) : { dates: [], currentStreak: 0, longestStreak: 0 };
      } catch (e) { return { dates: [], currentStreak: 0, longestStreak: 0 }; }
    },

    record: function (cid) {
      var d = this.get(cid);
      var today = new Date().toISOString().split('T')[0];
      if (d.dates.indexOf(today) === -1) d.dates.push(today);
      d.dates = d.dates.slice(-90);
      d.currentStreak = this._calc(d.dates);
      d.longestStreak = Math.max(d.longestStreak, d.currentStreak);
      localStorage.setItem(this._key(cid), JSON.stringify(d));
      return d;
    },

    _calc: function (dates) {
      if (!dates.length) return 0;
      var sorted = dates.slice().sort();
      var last = sorted[sorted.length - 1];
      var today = new Date().toISOString().split('T')[0];
      var yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      if (last !== today && last !== yesterday) return 0;
      var streak = 1;
      for (var i = sorted.length - 1; i > 0; i--) {
        var diff = (new Date(sorted[i]) - new Date(sorted[i - 1])) / 86400000;
        if (diff === 1) streak++;
        else break;
      }
      return streak;
    }
  };

  function initStreakTracker() {
    var container = document.getElementById('SaifStreakTracker');
    if (!container) return;
    var customer = readJSONByPrefix('SaifCourseCustomer');
    if (!customer || !customer.loggedIn) return;

    var data = SaifStreak.get(customer.customerId);
    var streakNum = container.querySelector('#SaifStreakCount');
    var longestNum = container.querySelector('#SaifStreakLongest');
    var heatmap = container.querySelector('#SaifStreakHeatmap');

    if (streakNum) streakNum.textContent = data.currentStreak;
    if (longestNum) longestNum.textContent = data.longestStreak;

    // Build 30-day heatmap
    if (heatmap) {
      var html = '';
      var today = new Date();
      for (var i = 29; i >= 0; i--) {
        var d = new Date(today);
        d.setDate(d.getDate() - i);
        var dateStr = d.toISOString().split('T')[0];
        var active = data.dates.indexOf(dateStr) !== -1;
        var label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        html += '<div class="saif-streak__day' + (active ? ' saif-streak__day--active' : '') +
          '" title="' + label + (active ? ' \u2714' : '') + '"></div>';
      }
      heatmap.innerHTML = html;
    }
  }

  // Studio takes count as practice too — called by saif-practice-studio.js
  // after a take saves. Resolves the customer itself so the studio doesn't
  // need to know about progress storage.
  window.SaifStreakRecordPractice = function () {
    var customer = readJSONByPrefix('SaifCourseCustomer');
    if (customer && customer.loggedIn && customer.customerId) {
      SaifStreak.record(customer.customerId);
    }
  };

  // Record practice on lesson complete
  var origToggle = SaifProgress.toggleComplete;
  SaifProgress.toggleComplete = function (cid, ch, lid) {
    var result = origToggle.call(this, cid, ch, lid);
    if (result) SaifStreak.record(cid); // completed = record practice
    return result;
  };

  /* ============================================
     LESSON BOOKMARKS
     ============================================ */
  var SaifBookmarks = {
    _key: function (cid, ch, lid) { return 'saif_bm_' + cid + '_' + ch + '_' + lid; },

    get: function (cid, ch, lid) {
      try {
        var d = localStorage.getItem(this._key(cid, ch, lid));
        return d ? JSON.parse(d) : [];
      } catch (e) { return []; }
    },

    add: function (cid, ch, lid, time, note) {
      var bm = this.get(cid, ch, lid);
      bm.push({ time: time, note: note, created: Date.now() });
      bm.sort(function (a, b) { return a.time - b.time; });
      localStorage.setItem(this._key(cid, ch, lid), JSON.stringify(bm));
      return bm;
    },

    remove: function (cid, ch, lid, index) {
      var bm = this.get(cid, ch, lid);
      bm.splice(index, 1);
      localStorage.setItem(this._key(cid, ch, lid), JSON.stringify(bm));
      return bm;
    }
  };

  function initBookmarks() {
    var container = document.getElementById('SaifLessonBookmarks');
    if (!container) return;

    var customer = readJSONByPrefix('SaifCourseCustomer');
    if (!customer || !customer.loggedIn) return;

    var params = new URLSearchParams(window.location.search);
    var courseHandle = params.get('course');
    var lessonId = params.get('lesson');
    if (!courseHandle || !lessonId) return;

    var list = document.getElementById('SaifBookmarkList');
    var addBtn = document.getElementById('SaifBookmarkAdd');
    var noteInput = document.getElementById('SaifBookmarkNote');

    function formatTime(s) {
      var m = Math.floor(s / 60), sec = Math.floor(s % 60);
      return m + ':' + (sec < 10 ? '0' : '') + sec;
    }

    function getVideoTime() {
      var iframe = document.querySelector('.saif-lesson__video iframe');
      // Can't access iframe time for cross-origin, return 0
      // Users type their own timestamp
      return 0;
    }

    function render() {
      var bookmarks = SaifBookmarks.get(customer.customerId, courseHandle, lessonId);
      if (!list) return;
      if (!bookmarks.length) {
        list.innerHTML = '<p class="saif-bookmarks__empty">No bookmarks yet. Add notes for moments you want to revisit.</p>';
        return;
      }
      var html = '';
      bookmarks.forEach(function (bm, i) {
        html += '<div class="saif-bookmarks__item">' +
          '<span class="saif-bookmarks__time">' + (bm.time ? formatTime(bm.time) : '--:--') + '</span>' +
          '<span class="saif-bookmarks__note">' + escapeHtml(bm.note) + '</span>' +
          '<button class="saif-bookmarks__remove" data-index="' + i + '" type="button" aria-label="Remove bookmark">&times;</button>' +
          '</div>';
      });
      list.innerHTML = html;

      list.querySelectorAll('.saif-bookmarks__remove').forEach(function (btn) {
        btn.addEventListener('click', function () {
          SaifBookmarks.remove(customer.customerId, courseHandle, lessonId, parseInt(btn.getAttribute('data-index')));
          render();
        });
      });
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var note = noteInput ? noteInput.value.trim() : '';
        if (!note) return;
        SaifBookmarks.add(customer.customerId, courseHandle, lessonId, 0, note);
        if (noteInput) noteInput.value = '';
        render();
      });
    }

    if (noteInput) {
      noteInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); if (addBtn) addBtn.click(); }
      });
    }

    render();
  }

  /* ============================================
     WEEKLY CHALLENGE
     ============================================ */
  function initWeeklyChallenge() {
    var container = document.getElementById('SaifWeeklyChallenge');
    if (!container) return;

    var challenges = [];
    container.querySelectorAll('[data-challenge]').forEach(function (el) {
      challenges.push({
        el: el,
        title: el.getAttribute('data-challenge-title'),
        start: el.getAttribute('data-challenge-start')
      });
    });

    if (!challenges.length) return;

    // Determine current week's challenge
    var now = new Date();
    var activeIndex = 0;

    for (var i = challenges.length - 1; i >= 0; i--) {
      var start = new Date(challenges[i].start);
      if (now >= start) { activeIndex = i; break; }
    }

    // Show only the active challenge
    challenges.forEach(function (c, i) {
      c.el.hidden = (i !== activeIndex);
    });

    // Calculate days remaining in this challenge week
    var nextStart = challenges[activeIndex + 1] ? new Date(challenges[activeIndex + 1].start) : null;
    var daysLeft = container.querySelector('#SaifChallengeDaysLeft');
    if (daysLeft && nextStart) {
      var diff = Math.ceil((nextStart - now) / 86400000);
      daysLeft.textContent = diff + ' day' + (diff !== 1 ? 's' : '') + ' left';
    }
  }

  /* ============================================
     LESSON SEARCH
     ============================================ */
  function initLessonSearch() {
    var input = document.getElementById('SaifLessonSearch');
    if (!input) return;

    input.addEventListener('input', function () {
      var query = input.value.toLowerCase().trim();
      var lessons = document.querySelectorAll('.saif-lesson__sidebar-lesson');
      var modules = document.querySelectorAll('.saif-lesson__sidebar-module');

      if (!query) {
        lessons.forEach(function (l) { l.style.display = ''; });
        modules.forEach(function (m) { m.style.display = ''; });
        return;
      }

      lessons.forEach(function (l) {
        var title = (l.textContent || '').toLowerCase();
        l.style.display = title.indexOf(query) !== -1 ? '' : 'none';
      });

      // Hide module headers if all their lessons are hidden
      modules.forEach(function (mod) {
        var visible = mod.querySelectorAll('.saif-lesson__sidebar-lesson:not([style*="display: none"])');
        mod.style.display = visible.length > 0 ? '' : 'none';
      });
    });
  }

  /* ============================================
     LESSON COMPLETION CELEBRATION
     ============================================ */
  function showCelebration(isModuleComplete) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var el = document.getElementById('SaifCelebration');
    var canvas = document.getElementById('SaifConfettiCanvas');
    var titleEl = document.getElementById('SaifCelebrationTitle');
    var textEl = document.getElementById('SaifCelebrationText');
    if (!el) return;

    if (isModuleComplete) {
      titleEl.textContent = (window.SaifI18n && window.SaifI18n.module_complete) || 'Module Complete!';
      textEl.textContent = (window.SaifI18n && window.SaifI18n.module_complete_detail) || 'You finished an entire module. Incredible progress.';
    } else {
      titleEl.textContent = (window.SaifI18n && window.SaifI18n.lesson_complete) || 'Lesson Complete!';
      var messages = [
        'Great work. Keep the momentum going.',
        'One step closer to mastery.',
        'Consistency is the key. You\'re doing it.',
        'Beautiful progress. On to the next one.'
      ];
      textEl.textContent = messages[Math.floor(Math.random() * messages.length)];
    }

    el.hidden = false;

    // Wire up close interactions (idempotent — guard so we only bind once)
    if (!el.dataset.dismissBound) {
      var dismiss = function () {
        el.hidden = true;
        if (autoHideTimer) { clearTimeout(autoHideTimer); autoHideTimer = null; }
      };
      var closeBtn = document.getElementById('SaifCelebrationClose');
      if (closeBtn) closeBtn.addEventListener('click', dismiss);
      // Click on the dimmed backdrop (but not the content card) dismisses
      el.addEventListener('click', function (evt) {
        if (evt.target === el) dismiss();
      });
      // Esc dismisses
      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && !el.hidden) dismiss();
      });
      el.dataset.dismissBound = '1';
    }

    // Confetti
    if (canvas) {
      var ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      var particles = [];
      var colors = ['#B8860B', '#D4A84B', '#2D6A5A', '#3D8B77', '#FAF7F2'];

      for (var i = 0; i < 80; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: -20 - Math.random() * 200,
          w: 4 + Math.random() * 6,
          h: 4 + Math.random() * 6,
          vx: (Math.random() - 0.5) * 4,
          vy: 2 + Math.random() * 4,
          rot: Math.random() * 360,
          rotV: (Math.random() - 0.5) * 10,
          color: colors[Math.floor(Math.random() * colors.length)],
          life: 1
        });
      }

      var frame = 0;
      function draw() {
        if (frame > 120) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particles.forEach(function (p) {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.08;
          p.rot += p.rotV;
          p.life -= 0.008;
          if (p.life <= 0) return;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot * Math.PI / 180);
          ctx.globalAlpha = Math.max(0, p.life);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        });
        frame++;
        requestAnimationFrame(draw);
      }
      draw();
    }

    // Auto-hide after 4s (longer to give users time to read)
    var autoHideTimer = setTimeout(function () {
      el.hidden = true;
      autoHideTimer = null;
    }, 4000);
  }

  // Patch the complete button to trigger celebration
  var origBindComplete = bindCompleteButton;
  bindCompleteButton = function (customerId, courseHandle, lessonId, config) {
    var btn = document.getElementById('SaifLessonCompleteBtn');
    if (!btn) return;

    var isCompleted = SaifProgress.isCompleted(customerId, courseHandle, lessonId);
    updateCompleteButton(btn, isCompleted, config);

    btn.addEventListener('click', function () {
      var wasCompleted = SaifProgress.isCompleted(customerId, courseHandle, lessonId);
      var nowCompleted = SaifProgress.toggleComplete(customerId, courseHandle, lessonId);
      updateCompleteButton(btn, nowCompleted, config);

      // Re-render sidebar
      var courseData = readJSON('SaifCourseData');
      if (courseData && courseData[courseHandle]) {
        var curriculum = typeof courseData[courseHandle].curriculum === 'string'
          ? JSON.parse(courseData[courseHandle].curriculum)
          : courseData[courseHandle].curriculum;
        renderSidebar(curriculum, lessonId, courseHandle, customerId);

        if (nowCompleted && !wasCompleted) {
          // Check if module is complete
          var isModuleComplete = false;
          if (curriculum && curriculum.modules) {
            curriculum.modules.forEach(function (mod) {
              if (!mod.lessons) return;
              var inThisModule = mod.lessons.some(function (l) { return l.id === lessonId; });
              if (!inThisModule) return;
              var allDone = mod.lessons.every(function (l) {
                return SaifProgress.isCompleted(customerId, courseHandle, l.id);
              });
              if (allDone) isModuleComplete = true;
            });
          }
          showCelebration(isModuleComplete);

          // Check for full course completion
          var pct = SaifProgress.getPercentage(customerId, courseHandle, (courseData[courseHandle].lessonCount || flattenLessons(curriculum).length));
          if (pct >= 100) {
            setTimeout(function () { showCertificate(courseData[courseHandle].title || courseHandle, customerId); }, 2800);
          }
        }
      }
    });
  };

  /* ============================================
     INIT
     ============================================ */
  function init() {
    initLessonPage();
    initDashboard();
    initCourseDetail();
    initLastAccessed();
    initNotesCount();
    initTimeRemaining();
    initStreakTracker();
    initBookmarks();
    initWeeklyChallenge();
    initLessonSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   UPCOMING SESSIONS PANEL — renders bookings on /pages/my-courses
   ============================================================ */
(function () {
  'use strict';

  // Own copy — this IIFE is a separate scope and cannot see the helper in the first one.
  // Shopify.routes.root is "/" in English and "/ar/" in Arabic.
  function saifPath(p) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/+$/, '') + p;
  }

  function start() {
    var panel = document.getElementById('SaifBookingsPanel');
    var listEl = document.getElementById('SaifBookingsList');
    if (!panel || !listEl) return;

    var sync = window.SaifSync;
    if (!sync) return;

    var currentBookings=[];
    function canReschedule(b){var p=b.reschedulePolicy;return !!(p&&p.allowed&&Date.now()<=Date.parse(p.cutoffAt));}
    var modal = document.getElementById('SaifRescheduleModal');
    var modalForm = document.getElementById('SaifRescheduleForm');
    var modalBookingIdInput = document.getElementById('SaifRescheduleBookingId');
    var modalMsg = document.getElementById('SaifRescheduleMsg');
    var modalCancel = document.getElementById('SaifRescheduleCancel');
    if (modalCancel && modal) {
      modalCancel.addEventListener('click', function () { try { modal.close(); } catch (e) {} });
    }
    // Click on the dimmed backdrop (outside the content card) dismisses the dialog.
    if (modal) {
      modal.addEventListener('click', function (evt) {
        if (evt.target === modal) { try { modal.close(); } catch (e) {} }
      });
    }

    function render(items) {
      currentBookings=items||[];
      panel.hidden = false;
      if (!items || !items.length) {
        listEl.innerHTML = '<p class="saif-bookings-panel__empty">No upcoming sessions. <a href="' + saifPath('/pages/book-a-session') + '">Book one</a>.</p>';
        return;
      }
      listEl.innerHTML = items.map(function (b) {
        var when = b.start ? new Date(b.start) : null;
        var dateLabel = when
          ? when.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) +
            ' \u00b7 ' + when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
          : '';
        var statusBadge = b.status === 'reschedule_requested' || b.rescheduleRequest
          ? '<span class="saif-bookings-panel__status saif-bookings-panel__status--pending">Reschedule requested</span>'
          : '';
        var canManage = b.status === 'confirmed' || b.status === 'reschedule_requested';
        if (b.status === 'confirming' || b.status === 'rescheduling' || b.status === 'cancelling') statusBadge = '<span class="saif-bookings-panel__status saif-bookings-panel__status--pending">' + (b.status === 'confirming' ? 'Preparing your meeting' : b.status === 'rescheduling' ? 'Updating your session' : 'Cancelling your session') + '</span>';
        var join = /^https:\/\/meet\.google\.com\//.test(b.joinUrl || '') && canManage
          ? '<a class="saif-bookings-panel__resched" href="' + escapeAttr(b.joinUrl) + '" target="_blank" rel="noopener">Join on Google Meet</a>' : '';
        return '<article class="saif-bookings-panel__item">' +
          '<div class="saif-bookings-panel__when">' + escapeAttr(dateLabel) + statusBadge + '</div>' +
          '<div class="saif-bookings-panel__service">' + escapeAttr(b.service || '') + '</div>' +
          (canManage
            ? '<div class="saif-bookings-panel__actions">' + join +
                (canReschedule(b)?'<button type="button" class="saif-bookings-panel__resched" data-id="' + escapeAttr(b.id) + '">Request reschedule</button>':'<span>'+(b.reschedulePolicy&&b.reschedulePolicy.reason!=='RESCHEDULE_ANCHOR_UNAVAILABLE'?'Rescheduling closed · 24-hour notice required.':'Rescheduling needs an academy review.')+'</span>') +
              '</div>'
            : '') +
        '</article>';
      }).join('');
    }

    function escapeAttr(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
      });
    }

    listEl.addEventListener('click', function (evt) {
      var btn = evt.target.closest('button.saif-bookings-panel__resched[data-id]');
      if (!btn) return;
      var booking=currentBookings.find(function(b){return b.id===btn.getAttribute('data-id');});
      if(!booking||!canReschedule(booking)){render(currentBookings);return;}
      if (!modal || !modal.showModal) {
        alert((window.SaifI18n && window.SaifI18n.reschedule_no_dialog_support) || 'Sorry, your browser does not support the reschedule dialog.');
        return;
      }
      modalBookingIdInput.value = btn.getAttribute('data-id') || '';
      if (modalMsg) modalMsg.textContent = '';
      modalForm.reset();
      var policy=booking.reschedulePolicy,day=modalForm.querySelector('[name=requestedDate]'),today=new Intl.DateTimeFormat('en-CA',{timeZone:policy.timeZone,year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date()),fmt=new Intl.DateTimeFormat(undefined,{month:'short',day:'numeric',timeZone:'UTC'});
      day.min=[today,policy.startDate].sort().at(-1);day.max=policy.lastDate;
      modal.querySelector('.saif-reschedule-modal__hint').textContent='Choose '+fmt.format(new Date(policy.startDate+'T12:00:00Z'))+' – '+fmt.format(new Date(policy.lastDate+'T12:00:00Z'))+'. Toronto time. Requests close 24\u00a0hours before your session.';
      modalBookingIdInput.value = btn.getAttribute('data-id') || '';
      // Re-enable the submit button in case a prior attempt left it disabled.
      var openSubmit = modalForm.querySelector('.saif-reschedule-modal__submit');
      if (openSubmit) { openSubmit.disabled = false; openSubmit.textContent = 'Send request'; }
      try { modal.showModal(); } catch (e) { /* ignore */ }
      // Move focus into the dialog for keyboard + screen-reader users.
      var firstField = modalForm.querySelector('input[type="date"], textarea, input, select');
      if (firstField) { try { firstField.focus(); } catch (e) {} }
    });

    if (modalForm) {
      modalForm.addEventListener('submit', function (evt) {
        evt.preventDefault();
        if (!sync.bookings) return;
        var bookingId = modalBookingIdInput.value;
        var fd = new FormData(modalForm);
        var requestedDate = fd.get('requestedDate') || '';
        var requestedTime = fd.get('requestedTime') || '';
        var reason = (fd.get('reason') || '').toString().trim();
        if (!reason) { if (modalMsg) modalMsg.textContent = (window.SaifI18n && window.SaifI18n.reschedule_reason_required) || 'Please add a short reason.'; return; }
        var submitBtn = modalForm.querySelector('.saif-reschedule-modal__submit');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = (window.SaifI18n && window.SaifI18n.reschedule_sending) || 'Sending...'; }
        sync.bookings.requestReschedule(bookingId, requestedDate, requestedTime, reason).then(function (r) {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Send request'; }
          if (r && r.ok) {
            if (modalMsg) modalMsg.textContent = (window.SaifI18n && window.SaifI18n.reschedule_sent) || 'Sent. The instructor will reach out shortly.';
            // Refresh the list so the status badge shows up
            sync.bookings.list().then(function (rr) {
              if (rr && rr.ok) render(rr.upcoming || []);
            }).catch(function () { /* list refresh is best-effort */ });
            setTimeout(function () { try { modal.close(); } catch (e) {} }, 1200);
          } else {
            // Log the raw code for debugging; show the user a human sentence.
            if (window.console && console.error) console.error('Reschedule request failed:', (r && r.error) || 'unknown');
            if (modalMsg) modalMsg.textContent = ({RESCHEDULE_CLOSED:'Rescheduling closes 24\u00a0hours before your lesson.',OUTSIDE_RESCHEDULE_WEEK:'Choose a date within this lesson’s original week.',RESCHEDULE_ANCHOR_UNAVAILABLE:'Please contact the academy to review this lesson’s dates.',INVALID_DATE:'Choose a valid date within the shown week.',INVALID_TIME:'Enter a valid time.'})[r&&r.error] || (window.SaifI18n && window.SaifI18n.reschedule_failed) || 'We couldn\'t reschedule your session — please try again.';
          }
        }).catch(function () {
          // Network/parse failure — without this, the button stays stuck on
          // "Sending..." forever and the user can't retry.
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Send request'; }
          if (modalMsg) modalMsg.textContent = (window.SaifI18n && window.SaifI18n.reschedule_error_generic) || 'Something went wrong. Please try again.';
        });
      });
    }

    sync.bookings.list().then(function (r) {
      if (!r || !r.ok) return;
      render(r.upcoming || []);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
